/* Cue-King Table Builder — pool table customizer.
   Mounts on every [data-ck-builder] element. Config comes from a child
   <script type="application/json" data-ck-config>. data-mode="shopify" adds
   to the Shopify cart; anything else runs as a demo. */
(function () {
  "use strict";

  /* ---------- colour helpers ---------- */
  function hexToRgb(h) {
    h = h.replace("#", "");
    if (h.length === 3) h = h.split("").map(function (c) { return c + c; }).join("");
    var n = parseInt(h, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  function rgbToHex(r) {
    return "#" + r.map(function (v) {
      v = Math.max(0, Math.min(255, Math.round(v)));
      return (v < 16 ? "0" : "") + v.toString(16);
    }).join("");
  }
  // amt > 0 mixes toward white, amt < 0 toward black
  function shade(hex, amt) {
    var c = hexToRgb(hex), t = amt < 0 ? 0 : 255, p = Math.abs(amt);
    return rgbToHex(c.map(function (v) { return v + (t - v) * p; }));
  }
  function luminance(hex) {
    var c = hexToRgb(hex);
    return (0.299 * c[0] + 0.587 * c[1] + 0.114 * c[2]) / 255;
  }

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  /* ---------- pricing ---------- */
  function money(cfg, minor) {
    try {
      return new Intl.NumberFormat(cfg.locale || "en-GB", {
        style: "currency", currency: cfg.currency || "GBP",
        minimumFractionDigits: minor % 100 === 0 ? 0 : 2
      }).format(minor / 100);
    } catch (e) { return (minor / 100).toFixed(2); }
  }
  function surcharge(cfg, item) {
    return item.price > 0 ? "+" + money(cfg, item.price) : "Included";
  }

  function find(list, id, fallbackId) {
    var i;
    for (i = 0; i < list.length; i++) if (list[i].id === id) return list[i];
    for (i = 0; i < list.length; i++) if (list[i].id === fallbackId) return list[i];
    return list[0];
  }
  // A finish with "designs": [...] only shows for those designs
  function forDesign(list, designId) {
    return list.filter(function (f) {
      return !f.designs || f.designs.indexOf(designId) !== -1;
    });
  }

  // Solid colours come in gloss or matt; the chosen sheen becomes part of the finish.
  function withSheen(f, sheen) {
    if (!f || f.kind !== "colour") return f;
    var sh = sheen === "matt" ? "matt" : "gloss", label = sh === "matt" ? "Matt" : "Gloss";
    return Object.assign({}, f, { id: f.id + "-" + sh, baseId: f.id, baseName: f.name, group: "colour", sheen: sh,
      kind: sh === "matt" ? "matt" : "paint", name: f.name + " (" + label + ")", variantName: label + " " + f.name });
  }
  function groupOf(f) { return f.group || f.kind; }
  // the photos a design can be seen in: side view first, then the other angles
  function viewsOf(d) { return d.views || (d.render ? [d.render] : []); }
  // Which finish group a finish is shown under: Gloss, Matt, Wood veneer or Metal.
  function tabOf(f) { return f.sheen || (f.kind === "colour" ? "gloss" : f.kind === "paint" ? "gloss" : f.kind); }
  // Swatch value: a solid colour carries its sheen ("navy-blue|matt"); wood and metal are just their id.
  function pickValue(f) { return f.baseId ? f.baseId + "|" + f.sheen : f.id; }

  function resolve(cfg, st) {
    var design = find(cfg.designs, st.design);
    var def = design.defaults || {};
    var size = find(design.sizes, st.size, def.size);
    var bodies = forDesign(cfg.bodyFinishes, design.id);
    var stands = forDesign(cfg.standFinishes === "body" ? cfg.bodyFinishes : cfg.standFinishes, design.id);
    var zones = design.zones || {};
    var body = withSheen(find(bodies, st.body, def.body), st.bodySheen || def.bodySheen);
    var stand = withSheen(find(stands, st.stand, def.stand), st.standSheen || def.standSheen);
    var cloth = find(cfg.cloths, st.cloth, def.cloth);
    // table tops: any number can be bought; one of them (or none) is shown on the table in the picture
    var tops = (cfg.tops || []).filter(function (t) { return t.id !== "none"; });
    var picked = st.tops || (st.top && st.top !== "none" ? [st.top] : []);
    var chosen = tops.filter(function (t) { return picked.indexOf(t.id) >= 0; });
    var shown = chosen.filter(function (t) { return t.id === st.topShown; })[0] || null;
    var dels = cfg.delivery || [], delivery = dels.length ? find(dels, st.delivery, dels[0].id) : null;
    var sc = cfg.stairs || null, flights = sc ? Math.max(0, Math.min(sc.max + 1, parseInt(st.stairs, 10) || 0)) : 0;
    return {
      design: design, size: size,
      body: body, stand: stand, cloth: cloth, tops: tops, topsChosen: chosen, topShown: shown,
      delivery: delivery, deliveries: dels, stairCfg: sc, flights: flights,
      // some deliveries (outside UK mainland) and more stairs than allowed are quoted by enquiry only
      delEnquire: !!(delivery && delivery.enquiry), stairsEnquire: !!sc && flights > sc.max,
      enquire: !!(delivery && delivery.enquiry) || (!!sc && flights > sc.max),
      notes: String(st.notes || "").trim().slice(0, 500),
      variant: chooseVariant(size, [cloth.name, body.variantName || body.name, stand.variantName || stand.name, body.baseName || body.name, stand.baseName || stand.name],
        { cloth: [cloth.name], table: [body.variantName || body.name, body.baseName || body.name] }),
      bodies: bodies, stands: stands,
      bodyLabel: zones.body || "Body",
      standLabel: zones.stand || "Stand"
    };
  }

  function priceLines(cfg, r) {
    return [
      { label: r.design.name + (/table/i.test(r.design.name) ? ", " : " table, ") + r.size.label, amount: r.variant.price, base: true },
      { label: r.bodyLabel + ": " + r.body.name, amount: r.body.price },
      { label: r.standLabel + ": " + r.stand.name, amount: r.stand.price },
      { label: "Cloth: " + r.cloth.name, amount: r.cloth.price }
    ].concat(r.topsChosen.map(function (t) { return { label: t.name, amount: t.price }; }))
     .concat(r.delivery && !r.delEnquire ? [{ label: "Delivery & fitting: " + r.delivery.name, amount: r.delivery.price }] : [])
     .concat(r.flights && !r.enquire ? [{ label: "Stairs: " + r.flights + (r.flights > 1 ? " flights" : " flight"), amount: r.flights * r.stairCfg.price }] : []);
  }

  // Cart items for Shopify /cart/add.js. Surcharges go in as add-on variants
  // because line item properties cannot change a variant's price.
  function cartItems(cfg, r, ref) {
    // The variant already names the size, so the properties carry the colour choices
    var props = {};
    props[r.bodyLabel + " colour"] = r.body.name;      // e.g. "Navy Blue (Gloss)" or "Walnut"
    props[r.standLabel + " colour"] = r.stand.name;
    props["Cloth"] = r.cloth.name;
    if (r.topsChosen.length) props[r.topsChosen.length > 1 ? "Table tops" : "Table top"] = r.topsChosen.map(function (t) { return t.name; }).join(", ");
    if (r.delivery) props["Delivery"] = r.delivery.name;
    if (r.notes) props["Installation notes"] = r.notes;
    if (r.stairCfg) props["Flights of stairs"] = String(r.flights);
    var extras = [];
    if (r.delivery) extras.push(["Delivery & fitting", r.delivery, 1]);
    if (r.flights && r.stairCfg) extras.push(["Stairs", { name: r.flights + (r.flights > 1 ? " flights" : " flight"), price: r.stairCfg.price, addonVariantId: r.stairCfg.addonVariantId }, r.flights]);
    var hasUpgrades = extras.length > 0 || [r.body, r.stand, r.cloth].concat(r.topsChosen).some(function (f) { return f.price > 0 && f.addonVariantId; });
    if (hasUpgrades) props["Build ref"] = ref;
    var items = [{ id: r.variant.id, quantity: 1, properties: props }];
    var merged = {};
    [[r.bodyLabel, r.body], [r.standLabel, r.stand], ["Cloth", r.cloth]].concat(r.topsChosen.map(function (t) { return ["Table top", t]; })).concat(extras).forEach(function (pair) {
      var f = pair[1];
      if (f.price > 0 && f.addonVariantId) {
        var k = String(f.addonVariantId);
        if (!merged[k]) {
          merged[k] = { id: f.addonVariantId, quantity: 0, properties: { "For build": ref } };
          items.push(merged[k]);
        }
        merged[k].quantity += pair[2] || 1;
        merged[k].properties["Upgrade"] = (merged[k].properties["Upgrade"] ? merged[k].properties["Upgrade"] + ", " : "") + pair[0] + ": " + f.name;
      }
    });
    return items;
  }

  function missingIds(r) {
    var miss = [];
    if (!r.variant.id) miss.push(r.design.name + " " + r.size.label);
    [r.body, r.stand, r.cloth].concat(r.topsChosen).concat([r.delivery || {}, r.flights && r.stairCfg ? { name: "Stairs", price: r.stairCfg.price, addonVariantId: r.stairCfg.addonVariantId } : {}]).forEach(function (f) {
      if (f.price > 0 && !f.addonVariantId) miss.push(f.name + " upgrade");
    });
    return miss;
  }

  /* ---------- Shopify products ---------- */
  // A design names its Shopify product ("product": handle). Sizes, prices and variant IDs
  // are read from that product, so they always match what checkout charges.
  function sizesFromProduct(cfg, p) {
    var names = (p.options || []).map(function (o) { return typeof o === "string" ? o : o.name; });
    var idx = 0;
    for (var i = 0; i < names.length; i++) if (/size/i.test(names[i])) { idx = i; break; }
    var order = [], groups = {};
    (p.variants || []).forEach(function (v) {
      var val = (v.options && v.options[idx]) || v.title;
      if (!groups[val]) { groups[val] = []; order.push(val); }
      groups[val].push(v);
    });
    return order.map(function (val) {
      var vs = groups[val], prices = vs.map(function (v) { return v.price; });
      var first = vs.filter(function (v) { return v.available !== false; })[0] || vs[0];
      return {
        id: val, label: val, bed: (cfg.beds || {})[val] || "",
        price: Math.min.apply(null, prices), priceVaries: Math.max.apply(null, prices) !== Math.min.apply(null, prices),
        variantId: first.id, variants: vs, optionIndex: idx, optionNames: names
      };
    });
  }
  // If the product also has cloth or colour options, pick the variant whose values match
  // the customer's choices; otherwise the size's first available variant.
  // Picks the variant that best matches the build. When the product's options are named (a "Cloth"
  // option, a "Table/Colour" option), each is matched only against that choice, so a black cloth
  // can't be mistaken for a black table.
  function chooseVariant(size, wanted, byOption) {
    if (!size.variants) return { id: size.variantId, price: size.price };
    var low = function (a) { return a.map(function (w) { return String(w).toLowerCase(); }); };
    var want = low(wanted), names = size.optionNames || [];
    var best = null, bestScore = -1;
    size.variants.forEach(function (v) {
      var score = (v.available === false ? 0 : 0.5);
      (v.options || []).forEach(function (o, i) {
        if (i === size.optionIndex) return;
        var n = String(names[i] || ""), pool = want;
        if (byOption && /cloth|felt|baize/i.test(n)) pool = low(byOption.cloth);
        else if (byOption && /table|colou?r|finish/i.test(n)) pool = low(byOption.table);
        if (pool.indexOf(String(o).toLowerCase()) !== -1) score += 1;
      });
      if (score > bestScore) { best = v; bestScore = score; }
    });
    return { id: best.id, price: best.price, title: best.title };
  }

  /* ---------- table drawing ---------- */
  // Inner felt quad (perspective): back-left, back-right, front-right, front-left
  var FELT = [[196, 158], [604, 158], [684, 232], [116, 232]];
  function P(u, v) {
    var bx = FELT[0][0] + (FELT[1][0] - FELT[0][0]) * u;
    var fx = FELT[3][0] + (FELT[2][0] - FELT[3][0]) * u;
    var y = FELT[0][1] + (FELT[3][1] - FELT[0][1]) * v;
    return [bx + (fx - bx) * v, y];
  }
  function poly(pts) { return pts.map(function (p) { return p[0] + "," + p[1]; }).join(" "); }

  function turnedLeg(cx, top, scale, fill, edge) {
    var prof = [[0, 21], [14, 21], [22, 12], [40, 17], [58, 9], [74, 13], [84, 10], [96, 15]];
    var L = [], R = [];
    prof.forEach(function (p) {
      var y = top + p[0] * scale, w = p[1] * scale;
      L.push([cx - w, y]); R.unshift([cx + w, y]);
    });
    var hl = prof.map(function (p) { return [cx - p[1] * scale * 0.45, top + p[0] * scale]; });
    return '<polygon points="' + poly(L.concat(R)) + '" fill="' + fill + '" stroke="' + edge + '" stroke-width="1.2"/>' +
      '<polyline points="' + poly(hl) + '" fill="none" stroke="#fff" stroke-opacity=".18" stroke-width="3"/>';
  }

  function drawTable(uid, r) {
    var body = r.body.hex, stand = r.stand.hex, cloth = r.cloth.hex;
    var bodyEdge = shade(body, -0.35), standEdge = shade(stand, -0.35);
    // wood gets grain, metal gets brushed lines; paint stays plain
    var texFill = function (finish) {
      if (finish.kind === "wood") return ' fill="url(#' + uid + '-grain)" opacity="' + (luminance(finish.hex) > 0.5 ? 0.14 : 0.22) + '"';
      if (finish.kind === "metal") return ' fill="url(#' + uid + '-brush)" opacity="' + (luminance(finish.hex) > 0.5 ? 0.18 : 0.28) + '"';
      return null;
    };
    var grain = function (x, y, w, h, finish) {
      var f = texFill(finish);
      return f ? '<rect x="' + x + '" y="' + y + '" width="' + w + '" height="' + h + '"' + f + "/>" : "";
    };
    var grainPoly = function (pts, finish) {
      var f = texFill(finish);
      return f ? '<polygon points="' + poly(pts) + '"' + f + "/>" : "";
    };
    var d = r.design.shape || r.design.id;
    var floor = d === "apex" ? 420 : 482;
    var s = [];
    s.push('<svg viewBox="0 0 800 ' + (floor + 38) + '" role="img" aria-label="' + esc(r.design.name + " pool table, " + r.bodyLabel.toLowerCase() + " " + r.body.name + ", " + r.standLabel.toLowerCase() + " " + r.stand.name + ", " + r.cloth.name + " cloth") + '" xmlns="http://www.w3.org/2000/svg">');
    s.push('<defs>' +
      '<pattern id="' + uid + '-grain" width="160" height="12" patternUnits="userSpaceOnUse">' +
      '<path d="M0 3 Q40 1 80 4 T160 3 M0 8 Q50 10 90 7 T160 9" fill="none" stroke="#000" stroke-width="1"/></pattern>' +
      '<pattern id="' + uid + '-brush" width="240" height="5" patternUnits="userSpaceOnUse">' +
      '<path d="M0 1 H150 M60 3.5 H240" fill="none" stroke="#000" stroke-width=".7"/><path d="M20 2.2 H200" stroke="#fff" stroke-width=".6"/></pattern>' +
      '<radialGradient id="' + uid + '-sheen" cx="0.5" cy="0.35" r="0.6"><stop offset="0" stop-color="#fff" stop-opacity=".16"/><stop offset="1" stop-color="#fff" stop-opacity="0"/></radialGradient>' +
      '<radialGradient id="' + uid + '-shadow" cx="0.5" cy="0.5" r="0.5"><stop offset="0" stop-color="#000" stop-opacity=".32"/><stop offset="1" stop-color="#000" stop-opacity="0"/></radialGradient>' +
      '</defs>');

    // floor shadow
    s.push('<ellipse cx="400" cy="' + floor + '" rx="' + (d === "apex" ? 340 : 380) + '" ry="22" fill="url(#' + uid + '-shadow)"/>');

    // back legs / supports, behind the cabinet
    if (d === "classic") {
      s.push(turnedLeg(204, 372, 0.72, shade(stand, -0.28), shade(stand, -0.5)));
      s.push(turnedLeg(596, 372, 0.72, shade(stand, -0.28), shade(stand, -0.5)));
    } else if (d === "industrial") {
      s.push('<rect x="196" y="318" width="14" height="124" fill="' + shade(stand, -0.32) + '"/>');
      s.push('<rect x="590" y="318" width="14" height="124" fill="' + shade(stand, -0.32) + '"/>');
    }

    // top: rails, cushions, felt
    s.push('<polygon points="170,140 630,140 724,250 76,250" fill="' + shade(body, 0.06) + '" stroke="' + bodyEdge + '" stroke-width="1.2"/>');
    s.push('<polygon points="' + poly([[188, 152], [612, 152], [696, 238], [104, 238]]) + '" fill="' + shade(cloth, -0.22) + '"/>');
    s.push('<polygon points="' + poly(FELT) + '" fill="' + cloth + '"/>');
    s.push('<polygon points="' + poly(FELT) + '" fill="url(#' + uid + '-sheen)"/>');

    // sights (diamonds) on the long rails
    [0.125, 0.25, 0.375, 0.625, 0.75, 0.875].forEach(function (u) {
      s.push('<circle cx="' + (170 + 460 * u) + '" cy="146" r="1.8" fill="#d8c08a"/>');
      s.push('<circle cx="' + (76 + 648 * u) + '" cy="244" r="2.4" fill="#d8c08a"/>');
    });

    // baulk line at one fifth of the bed
    var b0 = P(0.2, 0), b1 = P(0.2, 1);
    s.push('<line x1="' + b0[0] + '" y1="' + b0[1] + '" x2="' + b1[0] + '" y2="' + b1[1] + '" stroke="#fff" stroke-opacity=".38" stroke-width="1"/>');

    // pockets
    var pk = [[188, 152, 10, 5], [400, 151, 9, 4.5], [612, 152, 10, 5], [104, 238, 14, 7], [400, 240, 12, 6.5], [696, 238, 14, 7]];
    pk.forEach(function (p) {
      s.push('<ellipse cx="' + p[0] + '" cy="' + p[1] + '" rx="' + p[2] + '" ry="' + p[3] + '" fill="#0b0b0c"/>');
    });

    // balls: reds and yellows racked, black in the middle, cue ball in baulk
    var balls = [], pattern = "RYRYBRYRYRYRYRY".split(""), n = 0;
    for (var k = 0; k < 5; k++) for (var j = 0; j <= k; j++) {
      var c = pattern[n++];
      balls.push({ u: 0.7 + k * 0.026, v: 0.5 + (j - k / 2) * 0.15, c: c === "R" ? "#c4182f" : c === "Y" ? "#f1c40f" : "#111" });
    }
    balls.push({ u: 0.12, v: 0.58, c: "#f7f5ef" });
    balls.sort(function (a, b2) { return a.v - b2.v; }).forEach(function (bl) {
      var p = P(bl.u, bl.v), rr = 4.3 + bl.v * 1.6;
      s.push('<circle cx="' + p[0].toFixed(1) + '" cy="' + (p[1] - rr * 0.4).toFixed(1) + '" r="' + rr.toFixed(1) + '" fill="' + bl.c + '"/>' +
        '<circle cx="' + (p[0] - rr * 0.35).toFixed(1) + '" cy="' + (p[1] - rr * 0.8).toFixed(1) + '" r="' + (rr * 0.32).toFixed(1) + '" fill="#fff" fill-opacity=".7"/>');
    });

    // front rail edge
    s.push('<rect x="76" y="250" width="648" height="16" fill="' + shade(body, -0.06) + '" stroke="' + bodyEdge + '" stroke-width="1"/>');
    s.push(grain(76, 250, 648, 16, r.body));

    if (d === "classic") {
      s.push('<rect x="84" y="266" width="632" height="106" fill="' + body + '"/>');
      s.push(grain(84, 266, 632, 106, r.body));
      [[110, 280], [410, 280]].forEach(function (p) {
        s.push('<rect x="' + p[0] + '" y="' + 284 + '" width="280" height="70" rx="3" fill="' + shade(body, 0.04) + '" stroke="' + shade(body, -0.22) + '" stroke-width="2"/>');
        s.push('<rect x="' + (p[0] + 6) + '" y="290" width="268" height="58" rx="2" fill="none" stroke="#fff" stroke-opacity=".12"/>');
      });
      s.push('<rect x="78" y="372" width="644" height="12" fill="' + shade(body, -0.14) + '" stroke="' + bodyEdge + '" stroke-width="1"/>');
      s.push(turnedLeg(112, 384, 1, stand, standEdge));
      s.push(turnedLeg(688, 384, 1, stand, standEdge));
      s.push('<ellipse cx="112" cy="480" rx="18" ry="3" fill="#000" fill-opacity=".25"/><ellipse cx="688" cy="480" rx="18" ry="3" fill="#000" fill-opacity=".25"/>');
    } else if (d === "apex") {
      // two-tone wedge body: accent colour on the left, main colour on the right, split on a diagonal
      var left = [[78, 266], [595, 266], [525, 287], [295, 287], [258, 414], [163, 414]];
      var right = [[595, 266], [722, 266], [637, 414], [542, 414], [505, 287], [525, 287]];
      s.push('<polygon points="' + poly(left) + '" fill="' + stand + '" stroke="' + standEdge + '" stroke-width="1" stroke-linejoin="round"/>');
      s.push(grainPoly(left, r.stand));
      s.push('<polygon points="' + poly(right) + '" fill="' + body + '" stroke="' + bodyEdge + '" stroke-width="1" stroke-linejoin="round"/>');
      s.push(grainPoly(right, r.body));
      // shade the underside between the legs and the inner faces of each leg
      s.push('<polygon points="295,287 505,287 503,293 297,293" fill="#000" fill-opacity=".12"/>');
      [[1, stand], [-1, body]].forEach(function (side) {
        var m = function (x) { return side[0] === 1 ? x : 800 - x; };
        var col = side[1];
        var hole = [[m(150), 300], [m(234), 300], [m(220), 350], [m(196), 350]];
        s.push('<polygon points="' + poly(hole) + '" fill="var(--ck-stage, #dce3e6)"/>');
        s.push('<polygon points="' + poly([[m(150), 300], [m(166), 300], [m(204), 350], [m(196), 350]]) + '" fill="' + shade(col, -0.22) + '"/>');
        s.push('<polygon points="' + poly([[m(150), 300], [m(234), 300], [m(232), 305], [m(152), 305]]) + '" fill="#000" fill-opacity=".18"/>');
        s.push('<polygon points="' + poly([[m(196), 350], [m(220), 350], [m(240), 378], [m(180), 378]]) + '" fill="' + shade(col, 0.14) + '"/>');
        s.push('<polyline points="' + poly(hole.concat([hole[0]])) + '" fill="none" stroke="' + shade(col, -0.35) + '" stroke-width="1"/>');
      });
      // inner leg faces catch less light
      s.push('<polygon points="295,287 258,414 272,414 305,290" fill="#000" fill-opacity=".1"/>');
      s.push('<polygon points="505,287 542,414 528,414 495,290" fill="#000" fill-opacity=".1"/>');
    } else if (d === "pedestal") {
      s.push('<rect x="80" y="266" width="640" height="72" fill="' + body + '"/>');
      s.push(grain(80, 266, 640, 72, r.body));
      s.push('<line x1="80" y1="324" x2="720" y2="324" stroke="' + shade(body, -0.25) + '" stroke-width="2"/>');
      [250, 550].forEach(function (cx) {
        s.push('<polygon points="' + poly([[cx - 58, 338], [cx + 58, 338], [cx + 78, 450], [cx - 78, 450]]) + '" fill="' + stand + '" stroke="' + standEdge + '" stroke-width="1.2"/>');
        s.push('<polygon points="' + poly([[cx + 10, 338], [cx + 58, 338], [cx + 78, 450], [cx + 14, 450]]) + '" fill="#000" fill-opacity=".1"/>');
        s.push('<rect x="' + (cx - 92) + '" y="450" width="184" height="18" fill="' + shade(stand, -0.12) + '" stroke="' + standEdge + '" stroke-width="1.2"/>');
      });
    } else {
      s.push('<rect x="80" y="266" width="640" height="52" fill="' + body + '"/>');
      s.push(grain(80, 266, 640, 52, r.body));
      s.push('<rect x="80" y="318" width="640" height="7" fill="' + stand + '" stroke="' + standEdge + '" stroke-width="1"/>');
      s.push('<rect x="118" y="438" width="564" height="9" fill="' + shade(stand, -0.06) + '" stroke="' + standEdge + '" stroke-width="1"/>');
      [100, 682].forEach(function (x) {
        s.push('<rect x="' + x + '" y="325" width="18" height="148" fill="' + stand + '" stroke="' + standEdge + '" stroke-width="1"/>');
        s.push('<rect x="' + (x + 4) + '" y="327" width="3" height="144" fill="#fff" fill-opacity="' + (r.stand.kind === "metal" ? ".35" : ".15") + '"/>');
        s.push('<rect x="' + (x - 3) + '" y="473" width="24" height="6" rx="1" fill="#1a1a1a"/>');
      });
    }
    s.push("</svg>");
    return s.join("");
  }

  /* ---------- colour science ---------- */
  // All lighting is done in linear light (as a camera records it) and converted back to sRGB.
  var S2L = new Float32Array(256), L2S = new Uint8ClampedArray(4096);
  (function () {
    for (var i = 0; i < 256; i++) { var c = i / 255; S2L[i] = c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); }
    for (var k = 0; k < 4096; k++) { var l = k / 4095; L2S[k] = Math.round(255 * (l <= 0.0031308 ? l * 12.92 : 1.055 * Math.pow(l, 1 / 2.4) - 0.055)); }
  })();
  function toLin(v) { v = v < 0 ? 0 : v > 255 ? 255 : v; var i = v | 0; return i >= 255 ? 1 : S2L[i] + (S2L[i + 1] - S2L[i]) * (v - i); }
  function toSrgb(l) { return L2S[(l <= 0 ? 0 : l >= 1 ? 4095 : l * 4095 + 0.5) | 0]; }
  function hexLin(hex) { var c = hexToRgb(hex); return [S2L[c[0]], S2L[c[1]], S2L[c[2]]]; }
  function smoothstep(a, b, x) { var t = (x - a) / (b - a); t = t < 0 ? 0 : t > 1 ? 1 : t; return t * t * (3 - 2 * t); }
  // soft shoulder so bright reflections roll off to white instead of clipping
  function shoulder(v) { return v <= 0.82 ? v : 0.82 + (v - 0.82) / (1 + (v - 0.82) / 0.18); }

  /* ---------- noise ---------- */
  function hash2(ix, iy) {
    var h = Math.imul(ix, 374761393) + Math.imul(iy, 668265263) | 0;
    h = Math.imul(h ^ (h >>> 13), 1274126177);
    h = h ^ (h >>> 16);
    return (h >>> 0) / 4294967295;
  }
  function vnoise(x, y) {
    var ix = Math.floor(x), iy = Math.floor(y), fx = x - ix, fy = y - iy;
    var a = hash2(ix, iy), b = hash2(ix + 1, iy), c = hash2(ix, iy + 1), d = hash2(ix + 1, iy + 1);
    var ux = fx * fx * (3 - 2 * fx), uy = fy * fy * (3 - 2 * fy);
    return a + (b - a) * ux + (c - a) * uy + (a - b - c + d) * ux * uy;
  }
  function fbm(x, y, oct) {
    var v = 0, amp = 0.5, f = 1, norm = 0;
    for (var i = 0; i < oct; i++) { v += amp * vnoise(x * f, y * f); norm += amp; f *= 2.03; amp *= 0.5; }
    return v / norm;
  }

  /* ---------- wood ---------- */
  // A veneer is a thin slice cut from a log, laid in leaves about 20 cm wide. Growth rings are
  // cylinders round the log's centre: where the slice cuts across them you get the arched
  // "cathedral" figure of crown-cut veneer, elsewhere close straight lines. Each ring is light
  // earlywood that darkens into a band of dense latewood and ends at a crisp line. Pores run as
  // short dashes along the grain, rays show as small flecks, and long streaks of lighter and darker
  // wood run through it. x runs along the grain, in render pixels (about 1.3 mm each).
  //   g.ring: ring spacing in pixels   g.lw: latewood share of a ring   g.line: crispness of the ring line
  //   g.flat: how much cathedral figure   g.contrast   g.figure / g.streak: long streaks   g.flame: ring-to-ring colour swing
  //   g.pores, g.porous (pores only in earlywood), g.speckle, g.flecks, g.fine, g.warp, g.ribbon, g.mottle
  function woodTone(x, y, g, board) {
    var P = g.leaf || 150, yy = y + (hash2(board, 3) - 0.5) * P;
    var leaf = Math.floor(yy / P), ly = yy - leaf * P - P / 2;
    var lb = board * 64 + (leaf & 63);
    var h1 = hash2(lb, 11), h2 = hash2(lb, 23), h3 = hash2(lb, 37), h4 = hash2(lb, 51), o = lb * 57.31;
    var flat = g.flat || 0, warp = g.warp || 0;
    // the grain wanders: long gentle waves plus a little local wobble
    var wy = ly + (fbm(x * 0.0016 + o, ly * 0.006, 3) - 0.5) * warp * 2 + (fbm(x * 0.012 + o, ly * 0.05, 2) - 0.5) * warp * 0.25;
    // where this leaf was cut from the log: close to the heart gives tight arches
    // some leaves are cut through the heart of the log and show a cathedral arch; the rest are cut
    // to one side, where the rings show as close straight stripes (more of these in rift-cut woods)
    var arch = hash2(lb, 71) < flat;
    var y0 = arch ? (h1 - 0.5) * P * 0.5 : (h1 < 0.5 ? -1 : 1) * (P * 0.6 + h1 * P * 1.5);
    var dz = 10 + h2 * 40 + (Math.sin(x * 0.005 * (0.6 + h3) + h1 * 6.3) * 0.5 + 0.5) * 22 * flat
      + (fbm(x * 0.0015 + o, 1.7, 3) - 0.5) * 40 * flat;
    var dy = wy - y0, r = Math.sqrt(dy * dy + dz * dz);
    var spacing = (g.ring || 4) * (0.8 + 0.45 * h4);
    // rings vary in width from year to year: wide and narrow bands
    var t = r / spacing + (fbm(r * 0.012 + o, h2 * 9, 3) - 0.5) * 9 + (fbm(x * 0.004 + o, wy * 0.03, 2) - 0.5) * 0.7;
    var k = Math.floor(t), f = t - k;
    var rh = hash2(k, lb + 3), rw = hash2(k, lb + 5);
    var lw = (g.lw || 0.3) * (0.6 + 0.8 * rw), crisp = g.line === undefined ? 0.5 : g.line;
    // ring profile: earlywood, a darkening latewood band, a sharp line where the next ring starts
    var late = smoothstep(1 - lw - 0.15, 1 - lw * 0.25, f);
    var edge = Math.exp(-Math.pow((1 - f) / 0.06, 2)) + Math.exp(-Math.pow(f / 0.03, 2)) * 0.5;
    var c = g.contrast || 0.3, strength = 0.25 + 0.75 * Math.pow(rh, 1.4);
    var tone = 0.62 - c * strength * (0.45 * late + crisp * 0.55 * edge) + c * 0.08 * (1 - f) * (1 - late);
    // flame: neighbouring rings change colour, strongly in ironwood
    if (g.flame) tone += (hash2(Math.floor(t / 3), lb + 13) - 0.5) * g.flame + (rh - 0.5) * g.flame * 0.35;
    // long streaks of lighter and darker wood, and broad colour shifts
    tone += (fbm(x * 0.0012 + o, wy * 0.035, 4) - 0.5) * (g.figure || 0);
    tone += (fbm(x * 0.0004 + o + 9, wy * 0.008, 3) - 0.5) * (g.streak || 0);
    tone += (fbm(r * 0.02 + o, 3.3, 3) - 0.5) * (g.figure || 0) * 0.8;                   // bands of lighter and darker growth
    tone += (fbm(x * 0.0009 + o, wy * 0.09, 3) - 0.5) * (g.figure || 0) * 0.9;           // stripes of darker grain along the board
    tone += (h4 - 0.5) * 0.05;                                                        // leaf-to-leaf shade
    tone -= Math.exp(-Math.pow((Math.abs(ly) - P / 2) / 0.7, 2)) * 0.025;               // faint joint between leaves
    if (g.ribbon) tone += Math.sin(wy * 0.09 + fbm(x * 0.003 + o, wy * 0.004, 2) * 6) * g.ribbon * 0.06;   // ribbon stripe
    if (g.mottle) tone += (fbm(x * 0.03 + o, wy * 0.08, 3) - 0.5) * g.mottle;
    // fibre: very fine lines along the grain
    var fine = g.fine === undefined ? 0.05 : g.fine;
    tone += ((vnoise(x * 0.05 + o, wy * 1.7) - 0.5) * 0.7 + (vnoise(x * 0.13 + o, wy * 3.3 + 5) - 0.5) * 0.3) * fine * 2;
    // pores: short dark dashes along the grain (ring-porous woods only in the earlywood)
    if (g.pores) {
      var pn = vnoise(x * 0.11 + o, wy * 1.9 + 7.3) * vnoise(x * 0.031 + 3 + o, wy * 0.9);
      var pz = g.porous ? (1 - late) * (0.4 + 0.6 * smoothstep(0, 0.35, f)) : 1;
      tone -= g.pores * 0.5 * smoothstep(0.33, 0.6, pn) * pz;
    }
    // open-grain speckle: tiny elongated pits, dark with a light edge
    if (g.speckle) {
      var sp = vnoise(x * 0.32 + o, wy * 1.4 + 3.1);
      tone -= g.speckle * 0.3 * smoothstep(0.72, 0.92, sp);
      tone += g.speckle * 0.06 * smoothstep(0.8, 0.95, vnoise(x * 0.35 + 13, wy * 1.5 + o));
    }
    // ray flecks: small lighter flakes across the grain
    if (g.flecks) {
      var fl = vnoise(x * 0.42 + 50 + o, wy * 0.22);
      tone += g.flecks * 0.08 * smoothstep(0.82, 0.96, fl);
    }
    return tone < 0 ? 0 : tone > 1 ? 1 : tone;
  }
  // Brushed metal: long fine scratches along the brushing direction. Around 1.
  function brushTone(x, y, board) {
    var o = board * 31.7;
    // fine parallel scratches, a few deeper ones, and a soft anisotropic sheen across the brushing
    var fine = (vnoise(x * 0.012 + o, y * 1.9) - 0.5) * 0.11 + (vnoise(x * 0.03, y * 4.1 + o) - 0.5) * 0.06;
    var deep = Math.pow(vnoise(x * 0.004 + o, y * 0.7 + 3), 8) * 0.22;
    var sheen = (fbm(x * 0.0015 + o, y * 0.04, 3) - 0.5) * 0.12;
    return 1 + fine - deep + sheen;
  }
  // Felt: fine fibres and faint mottling. Around 1.
  function feltTone(x, y) {
    return 1 + (vnoise(x * 0.85, y * 0.85) - 0.5) * 0.06 + (vnoise(x * 2.1 + 9, y * 2.1) - 0.5) * 0.03 + (fbm(x * 0.012, y * 0.012, 3) - 0.5) * 0.06;
  }

  var lutCache = {};
  function paletteLut(finish) {
    if (lutCache[finish.id]) return lutCache[finish.id];
    var stops = (finish.palette || [shade(finish.hex, -0.35), finish.hex, shade(finish.hex, 0.25)]).map(hexToRgb)
      .sort(function (a, b) { return (a[0] * .3 + a[1] * .59 + a[2] * .11) - (b[0] * .3 + b[1] * .59 + b[2] * .11); });
    var lut = new Float32Array(256 * 3);
    for (var i = 0; i < 256; i++) {
      var pos = i / 255 * (stops.length - 1), k = Math.min(stops.length - 2, Math.floor(pos)), f = pos - k;
      for (var c = 0; c < 3; c++) lut[i * 3 + c] = toLin(stops[k][c] + (stops[k + 1][c] - stops[k][c]) * f);
    }
    return (lutCache[finish.id] = lut);
  }

  // Real veneer: photographic wood scans (one per species) decoded to linear colour once.
  var woodTex = {};
  function loadWood(f) {
    if (!f || f.kind !== "wood" || !f.texture || typeof document === "undefined") return Promise.resolve();
    if (!woodTex[f.id]) woodTex[f.id] = loadImage(f.texture).then(function (im) {
      var px = pixels(im, 1), n = px.width * px.height, lin = new Float32Array(n * 3);
      for (var i = 0; i < n; i++) { lin[i * 3] = S2L[px.data[i * 4]]; lin[i * 3 + 1] = S2L[px.data[i * 4 + 1]]; lin[i * 3 + 2] = S2L[px.data[i * 4 + 2]]; }
      f._tex = { w: px.width, h: px.height, lin: lin, k: px.width / (f.textureMm || 1000) * 1.3 };    // texture px per render px (1.3 mm)
    }).catch(function () {});
    return woodTex[f.id];
  }
  function sampleWood(t, x, y, board) {
    // each board takes its own part of the veneer; bilinear, wrapping (the scans tile seamlessly)
    x = x * t.k + hash2(board, 91) * t.w * 7; y = y * t.k + hash2(board, 92) * t.h * 7;
    var x0 = Math.floor(x), y0 = Math.floor(y), fx = x - x0, fy = y - y0, w = t.w, h = t.h, L = t.lin;
    var xa = ((x0 % w) + w) % w, xb = (xa + 1) % w, ya = ((y0 % h) + h) % h, yb = (ya + 1) % h;
    var p = (ya * w + xa) * 3, q = (ya * w + xb) * 3, r = (yb * w + xa) * 3, s = (yb * w + xb) * 3, out = [0, 0, 0];
    for (var c = 0; c < 3; c++) out[c] = (L[p + c] * (1 - fx) + L[q + c] * fx) * (1 - fy) + (L[r + c] * (1 - fx) + L[s + c] * fx) * fy;
    return out;
  }

  // A finish's own colour (linear) at a point on a board, before lighting.
  function finishColour(finish, gx, gy, board) {
    if (finish.kind === "wood" && finish._tex) return sampleWood(finish._tex, gx, gy, board);
    if (finish.kind === "wood") {
      var lut = paletteLut(finish), i = Math.round(woodTone(gx, gy, finish.grain || {}, board) * 255) * 3;
      return [lut[i], lut[i + 1], lut[i + 2]];
    }
    var t = finish._lin || (finish._lin = hexLin(finish.hex));
    if (finish.kind === "metal") { var b = brushTone(gx, gy, board); return [t[0] * b, t[1] * b, t[2] * b]; }
    if (finish.kind === "cloth") { var fl = feltTone(gx, gy); return [t[0] * fl, t[1] * fl, t[2] * fl]; }
    if (finish.kind === "matt") return [t[0] * 0.94 + 0.012, t[1] * 0.94 + 0.012, t[2] * 0.94 + 0.012];
    return t;
  }

  // Light a finish (linear colour c) with the photo's light.
  //   ratio: this point's brightness relative to the zone's typical brightness
  //   hi:    0..1, how far into the zone's brightest highlights this point is
  //   refl:  soft environment reflection (window light) for lacquered surfaces
  function lightFinish(kind, c, ratio, hi, refl) { var o = [0, 0, 0]; lightInto(kind, c[0], c[1], c[2], ratio, hi, refl, o); return o; }
  function lightInto(kind, c0, c1, c2, ratio, hi, refl, out) {
    var d = ratio, spec;
    if (kind === "matt") d = Math.pow(d, 0.8);
    if (kind === "cloth") d = Math.pow(d, 0.92);
    d = Math.min(d, kind === "cloth" ? 1.35 : 1.7);
    if (kind === "paint") spec = 0.16 * hi * hi + refl * (1 + 0.6 * Math.max(0, 1 - ratio));   // gloss: reflections show most on shaded faces
    else if (kind === "wood") spec = 0.2 * hi + refl * 0.3;           // satin lacquer over the grain
    else if (kind === "metal") {                                       // brushed metal: bright, contrasty reflections
      // metal shows the room: the window bands bright, the rest of the surroundings dark
      var env = Math.min(1.6, refl * 9);
      d = 0.5 * Math.pow(Math.min(ratio, 1.25), 1.6) + 0.42 * env;
      spec = 0.22 * hi * hi;
    }
    else if (kind === "matt") spec = 0.05 * hi;                       // matt: barely any shine
    else spec = 0;                                                    // cloth
    var r = c0 * d, g = c1 * d, b = c2 * d, m = Math.max(r, g, b);
    if (m > 0.78) { var k = shoulder(m) / m; r *= k; g *= k; b *= k; }   // brighten without bleaching the colour
    out[0] = shoulder(r + spec * 0.9); out[1] = shoulder(g + spec * 0.9); out[2] = shoulder(b + spec * 0.92);
  }

  // Swatch images drawn with the same colour and lighting code, so a swatch matches the table.
  var chipCache = {};
  function chipImage(finish, kind) {
    if (typeof document === "undefined") return "";
    if (kind !== "cloth" && finish.chip) return finish.chip;
    var f = kind === "cloth" ? { id: "cloth-" + finish.id, hex: finish.hex, kind: "cloth" } : finish;
    if (chipCache[f.id] !== undefined) return chipCache[f.id];
    var w = 144, h = 144, c = document.createElement("canvas");
    c.width = w; c.height = h;
    var g = c.getContext("2d"), img = g.createImageData(w, h), d = img.data;
    for (var y = 0; y < h; y++) for (var x = 0; x < w; x++) {
      var col = finishColour(f, x * 1.25 + 900, y * 1.25 + 300, 7);
      var ratio = 1.08 - 0.22 * (y / h) - 0.06 * (x / w);                          // soft light from top left
      var u = (x / w + (y / h) * 0.7) / 1.7;
      var refl = 0.16 * Math.exp(-Math.pow((u - 0.28) / 0.07, 2)) + 0.07 * Math.exp(-Math.pow((u - 0.4) / 0.03, 2));
      var out = lightFinish(f.kind, col, ratio, 0, refl), p = (y * w + x) * 4;
      d[p] = toSrgb(out[0]); d[p + 1] = toSrgb(out[1]); d[p + 2] = toSrgb(out[2]); d[p + 3] = 255;
    }
    g.putImageData(img, 0, 0);
    try { chipCache[f.id] = c.toDataURL("image/jpeg", 0.9); } catch (e) { chipCache[f.id] = ""; }
    return chipCache[f.id];
  }

  /* ---------- photo recolouring ---------- */
  // A design with "render" is shown as its real photo with new finishes:
  //   photo  the table on white
  //   mask   R = main zone, G = accent zone, B = cloth, A < 255 = outline against the white
  //   shade  the photo's lighting per zone with its old surface texture removed (R/G/B as above)
  //   light  per zone: typical ("ref"), highlight ("hi") brightness, linear
  //   grain  boards: polygons where the grain runs at an angle; each board gets its own pattern
  // render above the photo's resolution so grain and edges stay crisp: 2x on large screens, 1.5x on
  // phones (still sharper than a phone screen shows, with half the work)
  var SCALE = (typeof window !== "undefined" && Math.min(window.innerWidth || 1200, (window.screen && window.screen.width) || 1200) < 700) ? 1.5 : 2;
  var renderCache = {};
  function loadImage(src) {
    return new Promise(function (ok, fail) {
      var im = new Image();
      if (!/^data:/.test(src)) im.crossOrigin = "anonymous";
      im.onload = function () { ok(im); };
      im.onerror = function () { fail(new Error("Couldn't load " + src)); };
      im.src = src;
    });
  }
  function pixels(im, scale) {
    var c = document.createElement("canvas");
    c.width = im.naturalWidth * scale; c.height = im.naturalHeight * scale;
    var g = c.getContext("2d");
    g.imageSmoothingEnabled = true; g.imageSmoothingQuality = "high";
    g.drawImage(im, 0, 0, c.width, c.height);
    return g.getImageData(0, 0, c.width, c.height);
  }
  function inPoly(x, y, pts) {
    var inside = false;
    for (var i = 0, j = pts.length - 1; i < pts.length; j = i++) {
      var xi = pts[i][0], yi = pts[i][1], xj = pts[j][0], yj = pts[j][1];
      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) inside = !inside;
    }
    return inside;
  }
  // a reflected window: soft-edged band centred at c, half-width w, strength a
  function band(u, c, w, a) { return a * smoothstep(c - w - 0.012, c - w + 0.004, u) * (1 - smoothstep(c + w - 0.004, c + w + 0.012, u)); }

  // Things every picture shares (all are the same size): window reflections and a dither pattern.
  var shared = {};
  function sharedFor(w, h) {
    var k = w + "x" + h;
    if (shared[k]) return shared[k];
    var n = w * h, refl = new Float32Array(n), dith = new Float32Array(n);
    // the room's soft reflection varies slowly: work it out on a coarse grid and fill in between
    var G = 8, gw = Math.ceil(w / G) + 1, gh = Math.ceil(h / G) + 1, room = new Float32Array(gw * gh);
    for (var gy = 0; gy < gh; gy++) for (var gx = 0; gx < gw; gx++) room[gy * gw + gx] = 0.028 * (fbm(gx * G * 0.0035 + 3.1, gy * G * 0.009, 3) - 0.5) * 2;
    for (var q = 0; q < n; q++) {
      var qx = q % w, qy = (q / w) | 0, u = (qx + qy * 0.55) / w, v = qy / h;
      var fx = qx / G, fy = qy / G, ix = fx | 0, iy = fy | 0, tx = fx - ix, ty = fy - iy, r0 = iy * gw + ix;
      var rm = (room[r0] * (1 - tx) + room[r0 + 1] * tx) * (1 - ty) + (room[r0 + gw] * (1 - tx) + room[r0 + gw + 1] * tx) * ty;
      refl[q] = band(u, 0.31, 0.045, 0.09) + band(u, 0.405, 0.012, 0.06) + band(u, 0.83, 0.035, 0.07) + band(u, 0.905, 0.009, 0.045)
        + 0.025 * (1 - u) + 0.03 * (1 - v) + rm;
      dith[q] = hash2(q, 5) - 0.5;
    }
    return (shared[k] = { refl: refl, dith: dith });
  }

  // Keep the pictures of the last few designs ready; older ones are let go to save memory on phones.
  var renderOrder = [];
  function loadRender(render) {
    var key = render.photo;
    renderOrder = renderOrder.filter(function (k) { return k !== key; }); renderOrder.push(key);
    while (renderOrder.length > 6) delete renderCache[renderOrder.shift()];
    if (!renderCache[key]) {
      var srcs = [loadImage(render.photo), loadImage(render.mask), render.shade ? loadImage(render.shade) : null,
                  render.detail ? loadImage(render.detail).catch(function () { return null; }) : null];
      renderCache[key] = Promise.all(srcs).then(function (ims) {
        var base = pixels(ims[0], SCALE), mask = pixels(ims[1], SCALE), shadeImg = ims[2] ? pixels(ims[2], SCALE) : null;
        var detailImg = ims[3] ? pixels(ims[3], SCALE) : null;
        var w = base.width, h = base.height, n = w * h, md = mask.data;
        // lighting per zone: decoded from the shade map through a lookup table, only where the zone is
        var encs = ["main", "accent", "cloth"].map(function (k) { return render.light && render.light[k] && render.light[k].enc; });
        var luts = encs.map(function (e) {
          if (!e) return null;
          var t = new Float32Array(256);
          for (var v = 0; v < 256; v++) t[v] = Math.exp(e[0] + v / 255 * (e[1] - e[0]));
          return { t: t, k: (e[1] - e[0]) / 255 };
        });
        var sh = sharedFor(w, h), light = [new Float32Array(n), new Float32Array(n), new Float32Array(n)];
        var detail = new Float32Array(n), table = new Uint8Array(n);
        var bd = base.data, sd = shadeImg ? shadeImg.data : null, dd = detailImg ? detailImg.data : null;
        for (var i = 0; i < n; i++) {
          var p = i * 4;
          if (md[p] < 2 && md[p + 1] < 2 && md[p + 2] < 2) continue;          // background or kept as photographed
          table[i] = 1;
          var y = 0.2126 * S2L[bd[p]] + 0.7152 * S2L[bd[p + 1]] + 0.0722 * S2L[bd[p + 2]];
          detail[i] = dd ? Math.exp((dd[p] - 128) / 127 * 0.6 * 0.9) : 1;     // crisp edges, bevels and seams
          for (var z = 0; z < 3; z++) {
            if (md[p + z] < 2) continue;
            var L = luts[z];
            light[z][i] = sd && L ? L.t[sd[p + z]] * (1 + sh.dith[(i + z * 7919) % n] * L.k) : y;
          }
        }
        // which board each pixel belongs to (the grain runs a different way on each board)
        var board = new Uint8Array(n), regions = render.grain || [];
        var boxes = regions.map(function (rg) {
          var x0 = 1e9, x1 = -1e9, y0 = 1e9, y1 = -1e9;
          rg.poly.forEach(function (pt) { x0 = Math.min(x0, pt[0]); x1 = Math.max(x1, pt[0]); y0 = Math.min(y0, pt[1]); y1 = Math.max(y1, pt[1]); });
          return [x0 * SCALE, x1 * SCALE, y0 * SCALE, y1 * SCALE];
        });
        for (var yy = 0, k = 0; yy < h; yy++) for (var x = 0; x < w; x++, k++) {
          if (!table[k] || (md[k * 4] < 2 && md[k * 4 + 1] < 2)) continue;
          for (var r = 0; r < regions.length; r++) {
            var bx = boxes[r];
            if (x < bx[0] || x > bx[1] || yy < bx[2] || yy > bx[3]) continue;
            if (inPoly(x / SCALE, yy / SCALE, regions[r].poly)) { board[k] = r + 1; break; }
          }
        }
        var rot = [[1, 0]].concat(regions.map(function (rg) { var a = rg.angle * Math.PI / 180; return [Math.cos(a), Math.sin(a)]; }));
        var Lz = render.light || {};
        var zl = ["main", "accent", "cloth"].map(function (k2) {
          var v = Lz[k2] || {}, ref = v.ref || 0.5;
          return { ref: ref, hi: v.hi || Math.min(1, ref * 1.3) };
        });
        return { w: w, h: h, base: bd, mask: md, table: table, light: light, board: board, rot: rot, refl: sh.refl, dith: sh.dith,
          zl: zl, detail: detail, gamma: render.lightGamma || 1, layers: [null, null, null] };
      });
    }
    return renderCache[key];
  }

  // Colour layer for one zone and finish (linear), cached until that finish changes.
  function zoneLayer(data, z, fin) {
    var cached = data.layers[z];
    if (cached && cached.id === fin.id) return cached.arr;
    var w = data.w, n = w * data.h, arr = cached && cached.arr || new Float32Array(n * 3), m = data.mask, seed = z * 17;
    var GS = 2 / SCALE;      // grain is laid out in 2x pixels (1.3 mm each) whatever the picture's resolution
    for (var i = 0; i < n; i++) {
      if (m[i * 4 + z] < 2) continue;
      var bi = data.board[i], cs = data.rot[bi], x = (i % w) * GS, y = ((i / w) | 0) * GS;
      var c = finishColour(fin, x * cs[0] + y * cs[1], -x * cs[1] + y * cs[0], bi + seed);
      arr[i * 3] = c[0]; arr[i * 3 + 1] = c[1]; arr[i * 3 + 2] = c[2];
    }
    data.layers[z] = { id: fin.id, arr: arr };
    return arr;
  }

  function paintPhoto(canvas, data, render, r) {
    var g = canvas.getContext("2d"), img = data.img || (data.img = g.createImageData(data.w, data.h)), o = img.data;
    var cloth = { id: "cloth-" + r.cloth.id, hex: r.cloth.hex, kind: "cloth" };
    var fins = [r.body, r.stand, cloth];
    var layers = [zoneLayer(data, 0, fins[0]), zoneLayer(data, 1, fins[1]), zoneLayer(data, 2, fins[2])];
    var kinds = [fins[0].kind, fins[1].kind, fins[2].kind], out = [0, 0, 0], mk = data.mask, bs = data.base;
    var hiLo = data.zl.map(function (zl) { var top = Math.max(zl.hi, zl.ref * 1.25); return [Math.max(zl.ref * 1.15, top * 0.8), top * 1.05]; });
    var covDen = data.zl.map(function (zl) { return Math.max(20, 255 - toSrgb(Math.min(1, zl.ref))); });
    for (var i = 0, n = data.w * data.h; i < n; i++) {
      var p = i * 4;
      if (!data.table[i]) { o[p] = bs[p]; o[p + 1] = bs[p + 1]; o[p + 2] = bs[p + 2]; o[p + 3] = 255; continue; }
      var w0 = mk[p] / 255, w1 = mk[p + 1] / 255, w2 = mk[p + 2] / 255, ws = w0 + w1 + w2;
      var norm = ws > 1 ? 1 / ws : 1, keep = Math.max(0, 1 - ws);
      var a0 = S2L[bs[p]] * keep, a1 = S2L[bs[p + 1]] * keep, a2 = S2L[bs[p + 2]] * keep;
      var edge = mk[p + 3] < 200;   // outline pixel: part table, part white background
      for (var z = 0; z < 3; z++) {
        var wt = (z === 0 ? w0 : z === 1 ? w1 : w2) * norm;
        if (wt <= 0) continue;
        var zl = data.zl[z], lt = data.light[z][i];
        var ratio = (data.gamma === 1 ? lt / zl.ref : Math.pow(lt / zl.ref, data.gamma)) * data.detail[i];
        // only the zone's brightest spots count as highlights (a lit face is just brighter, not shiny)
        var hi = smoothstep(hiLo[z][0], hiLo[z][1], lt);
        var arr = layers[z];
        lightInto(kinds[z], arr[i * 3], arr[i * 3 + 1], arr[i * 3 + 2], ratio, hi, data.refl[i], out);
        if (edge && z < 2) {
          // blend toward the white background by how much of the pixel the table covers
          var lum = (bs[p] + bs[p + 1] + bs[p + 2]) / 3;
          var cov = Math.max(0, Math.min(1, (255 - lum) / covDen[z]));
          out[0] = out[0] * cov + (1 - cov); out[1] = out[1] * cov + (1 - cov); out[2] = out[2] * cov + (1 - cov);
        }
        a0 += out[0] * wt; a1 += out[1] * wt; a2 += out[2] * wt;
      }
      var dither = data.dith[i] * 0.0015;
      o[p] = toSrgb(a0 + dither); o[p + 1] = toSrgb(a1 + dither); o[p + 2] = toSrgb(a2 + dither); o[p + 3] = 255;
    }
    g.putImageData(img, 0, 0);
  }

  /* ---------- table tops (table tennis, air hockey, dining) drawn on the photo ---------- */
  // Colour of a finish for a flat top, in sRGB 0..255
  function topFinishRgb(f) {
    if (!f) return [20, 20, 20];
    if (f.kind === "wood") return hexToRgb((f.palette && f.palette[1]) || f.hex || "#8a6a45");
    var c = hexToRgb(f.hex || "#222222");
    return f.kind === "matt" ? c.map(function (v) { return Math.round(v * 0.94 + 3); }) : c;
  }
  // The top surface as a picture, W x D px, covering lenCm x depCm. look: table-tennis | air-hockey | dining
  function topTexture(look, lenCm, depCm, finish, woodImg) {
    var W = 1200, D = Math.round(W * depCm / lenCm), k = W / lenCm;
    var c = document.createElement("canvas"); c.width = W; c.height = D;
    var g = c.getContext("2d");
    if (look === "table-tennis") {
      g.fillStyle = "#121314"; g.fillRect(0, 0, W, D);
      g.strokeStyle = "#f2f2f2"; g.lineWidth = 2.6 * k; g.strokeRect(1.6 * k, 1.6 * k, W - 3.2 * k, D - 3.2 * k);
      g.fillStyle = "#e9e9e9"; g.fillRect(0, D / 2 - 0.9 * k, W, 1.8 * k);                    // centre line, end to end
    } else if (look === "air-hockey") {
      var R = 7 * k;                                                                           // the black rim
      g.fillStyle = "#0e0f10"; g.fillRect(0, 0, W, D);
      g.fillStyle = "#f4f5f5"; g.fillRect(R, R, W - 2 * R, D - 2 * R);
      g.fillStyle = "rgba(0,0,0,.16)";                                                         // air holes
      for (var y = R + 1.6 * k; y < D - R; y += 2.6 * k) for (var x = R + 1.6 * k; x < W - R; x += 2.6 * k) { g.beginPath(); g.arc(x, y, 0.16 * k, 0, 7); g.fill(); }
      g.strokeStyle = "#111"; g.fillStyle = "#111"; g.lineWidth = 1.6 * k;
      g.beginPath(); g.moveTo(W / 2, R); g.lineTo(W / 2, D - R); g.stroke();                   // centre line
      g.beginPath(); g.arc(W / 2, D / 2, 16 * k, 0, 7); g.stroke();
      g.beginPath(); g.arc(W / 2, D / 2, 1.8 * k, 0, 7); g.fill();
      [R, W - R].forEach(function (ex, i) {
        var dir = i ? -1 : 1;
        g.beginPath(); g.arc(ex, D / 2, 26 * k, -Math.PI / 2, Math.PI / 2, i === 1); g.stroke();   // goal arc
        g.beginPath(); g.arc(ex + dir * 17 * k, D / 2, 1.8 * k, 0, 7); g.fill();
        g.fillStyle = "#3a3c3e"; g.fillRect(i ? W - R : 0, D / 2 - 13 * k, R, 26 * k); g.fillStyle = "#111";   // goal slot in the rim
      });
      g.strokeStyle = "rgba(255,255,255,.35)"; g.lineWidth = 0.4 * k; g.strokeRect(R, R, W - 2 * R, D - 2 * R);   // rim's inner edge catching light
    } else {
      var rgb = topFinishRgb(finish);
      g.fillStyle = "rgb(" + rgb.join(",") + ")"; g.fillRect(0, 0, W, D);
      if (finish && finish.kind === "wood" && woodImg) {
        var tw = (finish.textureMm || 1000) / 10 * k, th = tw * woodImg.height / woodImg.width;
        for (var yy = 0; yy < D; yy += th) for (var xx = 0; xx < W; xx += tw) g.drawImage(woodImg, xx, yy, tw, th);
      }
    }
    // the light from the front left sliding across the surface; glossy tops show it more
    var gloss = look === "dining" ? (finish && (finish.kind === "paint" || finish.sheen === "gloss") ? 0.16 : finish && finish.kind === "metal" ? 0.12 : 0.05) : look === "table-tennis" ? 0.12 : 0.05;
    var sh = g.createLinearGradient(0, D, W * 0.7, 0);
    sh.addColorStop(0, "rgba(255,255,255," + gloss + ")"); sh.addColorStop(0.55, "rgba(255,255,255," + gloss * 0.25 + ")"); sh.addColorStop(1, "rgba(255,255,255,0)");
    g.fillStyle = sh; g.fillRect(0, 0, W, D);
    return c;
  }
  // Draw one textured triangle (texture points t0..t2 -> screen points s0..s2), affine
  function texTri(g, img, t0, t1, t2, s0, s1, s2) {
    var cx = (s0[0] + s1[0] + s2[0]) / 3, cy = (s0[1] + s1[1] + s2[1]) / 3, grow = function (p) { var dx = p[0] - cx, dy = p[1] - cy, l = Math.hypot(dx, dy) || 1; return [p[0] + dx / l * 1.1, p[1] + dy / l * 1.1]; };
    var a = grow(s0), b = grow(s1), c = grow(s2);
    g.save(); g.beginPath(); g.moveTo(a[0], a[1]); g.lineTo(b[0], b[1]); g.lineTo(c[0], c[1]); g.closePath(); g.clip();
    var x0 = t0[0], y0 = t0[1], x1 = t1[0] - x0, y1 = t1[1] - y0, x2 = t2[0] - x0, y2 = t2[1] - y0;
    var det = x1 * y2 - x2 * y1; if (Math.abs(det) < 1e-9) { g.restore(); return; }
    var u1 = s1[0] - s0[0], v1 = s1[1] - s0[1], u2 = s2[0] - s0[0], v2 = s2[1] - s0[1];
    var A = (u1 * y2 - u2 * y1) / det, B = (v1 * y2 - v2 * y1) / det, C = (u2 * x1 - u1 * x2) / det, Dd = (v2 * x1 - v1 * x2) / det;
    g.setTransform(A, B, C, Dd, s0[0] - A * x0 - C * y0, s0[1] - B * x0 - Dd * y0);
    g.drawImage(img, 0, 0); g.restore();
  }
  // Draws the chosen top sitting on the table in a photo. cal: the design's camera for each photo
  // (3x4 matrices to the 900x360 photo) and the outline of the table's top in cm.
  function drawTop(canvas, cal, vi, look, finish, woodImg) {
    var M = cal.m[vi], b = cal.box, g = canvas.getContext("2d"), k = canvas.width / 900;
    var proj = function (x, y, z) {
      var w = M[8] * x + M[9] * y + M[10] * z + M[11];
      return [(M[0] * x + M[1] * y + M[2] * z + M[3]) / w * k, (M[4] * x + M[5] * y + M[6] * z + M[7]) / w * k];
    };
    var spec = { "table-tennis": { e: 1.5, t: 2.6, side: [18, 19, 20] }, "air-hockey": { e: 1.5, t: 7, side: [16, 17, 18] }, dining: { e: 2.5, t: 3.2 } }[look] || { e: 2, t: 3 };
    var x0 = b.x0 - spec.e, x1 = b.x1 + spec.e, z0 = b.z0 - spec.e, z1 = b.z1 + spec.e, y0 = b.y + 0.2, y1 = b.y + spec.t;
    var side = spec.side || topFinishRgb(finish);
    var tex = topTexture(look, x1 - x0, z1 - z0, finish, woodImg), TW = tex.width, TD = tex.height;
    g.save(); g.setTransform(1, 0, 0, 1, 0, 0);
    // soft shadow the top throws onto the table just below it
    var a = proj(x0, y0, z1), bb = proj(x1, y0, z1), a2 = proj(x0, y0 - 5, z1), b2 = proj(x1, y0 - 5, z1);
    var sg = g.createLinearGradient(0, (a[1] + bb[1]) / 2, 0, (a2[1] + b2[1]) / 2);
    sg.addColorStop(0, "rgba(0,0,0,.30)"); sg.addColorStop(1, "rgba(0,0,0,0)");
    g.fillStyle = sg; g.beginPath(); g.moveTo(a[0], a[1]); g.lineTo(bb[0], bb[1]); g.lineTo(b2[0], b2[1]); g.lineTo(a2[0], a2[1]); g.closePath(); g.fill();
    // the slab's sides (only those facing the camera)
    var P = function (q) { return proj(q[0], q[1], q[2]); };
    var area = function (pts) { var s2 = 0; for (var i = 0; i < pts.length; i++) { var p1 = pts[i], p2 = pts[(i + 1) % pts.length]; s2 += p1[0] * p2[1] - p2[0] * p1[1]; } return s2; };
    var topQ = [[x0, y1, z0], [x0, y1, z1], [x1, y1, z1], [x1, y1, z0]];          // seen from above, counter-clockwise
    var ref = area(topQ.map(P)) > 0 ? 1 : -1;
    var faces = [
      { q: [[x0, y0, z1], [x1, y0, z1], [x1, y1, z1], [x0, y1, z1]], l: 0.82 },   // front
      { q: [[x1, y0, z0], [x0, y0, z0], [x0, y1, z0], [x1, y1, z0]], l: 0.7 },    // back
      { q: [[x1, y0, z1], [x1, y0, z0], [x1, y1, z0], [x1, y1, z1]], l: 0.62 },   // right end
      { q: [[x0, y0, z0], [x0, y0, z1], [x0, y1, z1], [x0, y1, z0]], l: 0.9 }     // left end (towards the light)
    ];
    faces.forEach(function (f) {
      var pts = f.q.map(P);
      if ((area(pts) > 0 ? 1 : -1) !== ref) return;
      var c = side.map(function (v) { return Math.round(Math.min(255, v * f.l + (look === "dining" ? 0 : 6))); });
      g.fillStyle = "rgb(" + c.join(",") + ")"; g.beginPath(); pts.forEach(function (p, i) { if (i) g.lineTo(p[0], p[1]); else g.moveTo(p[0], p[1]); }); g.closePath(); g.fill();
      // a thin highlight along the upper edge, as on a lacquered edge
      g.strokeStyle = "rgba(255,255,255," + (look === "dining" && finish && finish.kind === "matt" ? 0.08 : 0.2) + ")"; g.lineWidth = Math.max(1, k * 0.6);
      g.beginPath(); g.moveTo(pts[3][0], pts[3][1]); g.lineTo(pts[2][0], pts[2][1]); g.stroke();
    });
    // the top surface, in perspective: a fine grid of textured triangles over a solid base, so no seams show
    var tq = topQ.map(P), base = look === "table-tennis" ? "#121314" : look === "air-hockey" ? "#e9eaea" : "rgb(" + topFinishRgb(finish).join(",") + ")";
    g.fillStyle = base; g.beginPath(); tq.forEach(function (p, i) { if (i) g.lineTo(p[0], p[1]); else g.moveTo(p[0], p[1]); }); g.closePath(); g.fill();
    var NX = 24, NZ = 8;
    for (var i = 0; i < NX; i++) for (var j = 0; j < NZ; j++) {
      var u0 = i / NX, u1 = (i + 1) / NX, w0 = j / NZ, w1 = (j + 1) / NZ;
      var X0 = x0 + (x1 - x0) * u0, X1 = x0 + (x1 - x0) * u1, Z0 = z0 + (z1 - z0) * w0, Z1 = z0 + (z1 - z0) * w1;
      var s00 = proj(X0, y1, Z0), s10 = proj(X1, y1, Z0), s01 = proj(X0, y1, Z1), s11 = proj(X1, y1, Z1);
      var t00 = [u0 * TW, w0 * TD], t10 = [u1 * TW, w0 * TD], t01 = [u0 * TW, w1 * TD], t11 = [u1 * TW, w1 * TD];
      texTri(g, tex, t00, t10, t11, s00, s10, s11); texTri(g, tex, t00, t11, t01, s00, s11, s01);
    }
    g.setTransform(1, 0, 0, 1, 0, 0);
    if (look === "table-tennis") {                      // the net across the middle, with its posts
      var xc = (x0 + x1) / 2, nz0 = z0 - 3, nz1 = z1 + 3, ny = y1 + 15.25;
      var n = [proj(xc, y1, nz0), proj(xc, y1, nz1), proj(xc, ny, nz1), proj(xc, ny, nz0)];
      g.fillStyle = "rgba(14,15,16,.78)"; g.beginPath(); n.forEach(function (p, i) { if (i) g.lineTo(p[0], p[1]); else g.moveTo(p[0], p[1]); }); g.closePath(); g.fill();
      var tp = [proj(xc, ny - 1.4, nz0), proj(xc, ny - 1.4, nz1), proj(xc, ny, nz1), proj(xc, ny, nz0)];
      g.fillStyle = "rgba(235,235,235,.95)"; g.beginPath(); tp.forEach(function (p, i) { if (i) g.lineTo(p[0], p[1]); else g.moveTo(p[0], p[1]); }); g.closePath(); g.fill();
      [nz0, nz1].forEach(function (zz) {
        var pa = proj(xc - 1, y1, zz), pb = proj(xc + 1, y1, zz), pc = proj(xc + 1, ny + 1, zz), pd = proj(xc - 1, ny + 1, zz);
        g.fillStyle = "#0b0b0c"; g.beginPath(); g.moveTo(pa[0], pa[1]); g.lineTo(pb[0], pb[1]); g.lineTo(pc[0], pc[1]); g.lineTo(pd[0], pd[1]); g.closePath(); g.fill();
      });
    }
    g.restore();
  }

  /* ---------- UI ---------- */
  var uidCount = 0;

  function mount(root) {
    var cfgEl = root.querySelector("script[data-ck-config]");
    var cfg;
    try { cfg = JSON.parse(cfgEl.textContent); } catch (e) {
      root.innerHTML = '<p class="ck-error">The table builder could not load its settings. Check the JSON in the section file for a missing comma or quote.</p>';
      return;
    }
    var mode = root.getAttribute("data-mode") === "shopify" ? "shopify" : "demo";
    var uid = "ck" + (++uidCount);
    var st = { design: null, size: null, body: null, stand: null, cloth: null };
    var paintToken = 0;
    var routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";

    function prepare() {
      if (mode !== "shopify") return Promise.resolve();
      return Promise.all(cfg.designs.map(function (d) {
        if (!d.product) return null;
        return fetch(routeRoot + "products/" + encodeURIComponent(d.product) + ".js")
          .then(function (res) { return res.ok ? res.json() : null; })
          .catch(function () { return null; });
      })).then(function (products) {
        cfg.designs = cfg.designs.filter(function (d, i) {
          if (!products[i]) return false;
          d.sizes = sizesFromProduct(cfg, products[i]);
          return d.sizes.length > 0;
        });
      });
    }

    root.innerHTML = '<p class="ck-loading">Loading table options…</p>';
    prepare().then(start);

    function start() {
    if (!cfg.designs.length) {
      root.innerHTML = '<p class="ck-error">Table options are unavailable right now. Please contact us and we\'ll help you design your table.</p>';
      return;
    }
    var current = root.getAttribute("data-current-product");
    var firstDesign = cfg.designs.filter(function (d) { return current && d.product === current; })[0] || cfg.designs[0];
    st.design = firstDesign.id;

    root.innerHTML =
      '<div class="ck">' +
      '<section class="ck-stage" aria-live="polite">' +
      '<div class="ck-stage-head"><p class="ck-eyebrow">' + esc(cfg.eyebrow || "Design your table") + '</p><h2 class="ck-title" data-ck="title"></h2><p class="ck-sub" data-ck="sub"></p></div>' +
      '<div class="ck-photos" data-ck="photos"><div class="ck-canvas" data-ck="canvas"></div>' +
      '<button type="button" class="ck-arrow ck-arrow--prev" data-step="-1" aria-label="Previous angle">&#8249;</button>' +
      '<button type="button" class="ck-arrow ck-arrow--next" data-step="1" aria-label="Next angle">&#8250;</button>' +
      '<div class="ck-dots" data-ck="dots" role="group" aria-label="Angle"></div></div>' +
      '<div class="ck-spin" data-ck="spin" hidden><p class="ck-spin-msg" data-ck="spinMsg">Loading 360° view…</p>' +
      '<div class="ck-zoom"><button type="button" class="ck-zoom-btn" data-tilt="-0.21" aria-label="Look from higher">&#8593;</button>' +
      '<button type="button" class="ck-zoom-btn" data-tilt="0.21" aria-label="Look from lower">&#8595;</button>' +
      '<button type="button" class="ck-zoom-btn" data-zoom="0.82" aria-label="Zoom in">+</button>' +
      '<button type="button" class="ck-zoom-btn" data-zoom="1.22" aria-label="Zoom out">&minus;</button></div></div>' +
      '<div class="ck-ontable" data-ck="onTable" hidden><span class="ck-ontable-label">On the table</span><div class="ck-ontable-btns" data-ck="onTableBtns" role="group" aria-label="Top shown on the table"></div></div>' +
      '<div class="ck-viewbar"><div class="ck-seg" role="group" aria-label="View">' +
      '<button type="button" class="ck-seg-btn" data-view3d="photo" aria-pressed="true">Photo</button>' +
      '<button type="button" class="ck-seg-btn" data-view3d="spin" aria-pressed="false">360° spin</button></div>' +
      '<span class="ck-viewhint" data-ck="viewHint">Swipe for more angles</span></div>' +
      '<dl class="ck-spec" data-ck="spec"></dl>' +
      '</section>' +
      '<form class="ck-panel" novalidate>' +
      '<div class="ck-step" role="group"><div class="ck-legend"><button type="button" class="ck-step-head" data-toggle="designs" aria-expanded="false"><span class="ck-num"></span><span class="ck-step-title">Design</span> <span class="ck-chosen" data-ck="designName"></span><span class="ck-chev" aria-hidden="true"></span></button></div>' +
        '<div class="ck-step-body"><div class="ck-designs" data-ck="designs"></div></div></div>' +
      '<div class="ck-step" role="group"><div class="ck-legend"><button type="button" class="ck-step-head" data-toggle="sizes" aria-expanded="false"><span class="ck-num"></span><span class="ck-step-title">Size</span> <span class="ck-chosen" data-ck="sizeName"></span><span class="ck-chev" aria-hidden="true"></span></button></div>' +
        '<div class="ck-step-body"><div class="ck-sizes" data-ck="sizes"></div></div></div>' +
      '<div class="ck-step" role="group"><div class="ck-legend"><button type="button" class="ck-step-head" data-toggle="body" aria-expanded="false"><span class="ck-num"></span><span class="ck-step-title"><span data-ck="bodyLabel">Body</span> colour</span> <span class="ck-chosen" data-ck="bodyName"></span><span class="ck-chev" aria-hidden="true"></span></button></div>' +
        '<div class="ck-step-body"><div class="ck-picker" data-ck="body"></div></div></div>' +
      '<div class="ck-step" role="group"><div class="ck-legend"><button type="button" class="ck-step-head" data-toggle="stand" aria-expanded="false"><span class="ck-num"></span><span class="ck-step-title"><span data-ck="standLabel">Stand</span> colour</span> <span class="ck-chosen" data-ck="standName"></span><span class="ck-chev" aria-hidden="true"></span></button></div>' +
        '<div class="ck-step-body"><div class="ck-picker" data-ck="stand"></div></div></div>' +
      '<div class="ck-step" role="group"><div class="ck-legend"><button type="button" class="ck-step-head" data-toggle="cloth" aria-expanded="false"><span class="ck-num"></span><span class="ck-step-title">Cloth</span> <span class="ck-chosen" data-ck="clothName"></span><span class="ck-chev" aria-hidden="true"></span></button></div>' +
        '<div class="ck-step-body"><div class="ck-swatches ck-swatches--cloth" data-ck="cloth"></div></div></div>' +
      '<div class="ck-step" role="group" data-ck="topStep"><div class="ck-legend"><button type="button" class="ck-step-head" data-toggle="top" aria-expanded="false"><span class="ck-num"></span><span class="ck-step-title">Table top</span> <span class="ck-chosen" data-ck="topName"></span><span class="ck-chev" aria-hidden="true"></span></button></div>' +
        '<div class="ck-step-body"><p class="ck-sub-label">Choose as many as you like. Each one can be shown on the table in the picture.</p><div class="ck-tops" data-ck="top"></div></div></div>' +
      '<div class="ck-step" role="group" data-ck="delStep"><div class="ck-legend"><button type="button" class="ck-step-head" data-toggle="delivery" aria-expanded="false">' +
      '<span class="ck-num"></span><span class="ck-step-title">Delivery &amp; fitting</span> <span class="ck-chosen" data-ck="delName"></span><span class="ck-chev" aria-hidden="true"></span></button></div>' +
        '<div class="ck-step-body"><div class="ck-tops" data-ck="delivery"></div>' +
        '<div class="ck-stairs" data-ck="stairsWrap"><p class="ck-sub-label">Flights of stairs to the room <span class="ck-sub-note" data-ck="stairsNote"></span></p><div class="ck-stairs-row" data-ck="stairs"></div>' +
        '<p class="ck-enquire" data-ck="enquire" hidden></p></div>' +
        '<label class="ck-notes"><span class="ck-sub-label">Installation notes <span class="ck-sub-note">· optional</span></span>' +
        '<textarea data-ck="notes" rows="3" maxlength="500" placeholder="Anything our fitters should know"></textarea></label></div></div>' +
      '<div class="ck-summary"><ul class="ck-lines" data-ck="lines"></ul>' +
      '<div class="ck-total"><span>Total</span><strong data-ck="total"></strong></div>' +
      '<button type="submit" class="ck-add" data-ck="add">Add to basket</button>' +
      '<p class="ck-status" data-ck="status" role="status"></p>' +
      (cfg.note ? '<p class="ck-note">' + esc(cfg.note) + '</p>' : '') +
      '<pre class="ck-payload" data-ck="payload" hidden></pre></div>' +
      '</form></div>';

    var $ = function (k) { return root.querySelector('[data-ck="' + k + '"]'); };
    var form = root.querySelector("form");

    function radio(name, value, checked, inner, cls) {
      var id = uid + "-" + name + "-" + value;
      return '<label class="' + cls + '" for="' + id + '"><input type="radio" class="ck-sr" name="' + uid + "-" + name + '" id="' + id + '" value="' + esc(value) + '"' + (checked ? " checked" : "") + ">" + inner + "</label>";
    }

    function checkbox(name, value, checked, inner, cls) {
      var id = uid + "-" + name + "-" + value;
      return '<label class="' + cls + '" for="' + id + '"><input type="checkbox" class="ck-sr" name="' + uid + "-" + name + '" id="' + id + '" value="' + esc(value) + '"' + (checked ? " checked" : "") + ">" + inner + "<span class=\"ck-tick\" aria-hidden=\"true\"></span></label>";
    }

    function swatches(name, list, current) {
      return list.map(function (f) {
        var light = luminance(f.hex) > 0.75, chip = chipImage(f, name === "cloth" ? "cloth" : "");
        var value = pickValue(f);
        return radio(name, value, false,
          '<span class="ck-chip' + (light ? " ck-chip--light" : "") + (f.kind === "metal" ? " ck-chip--metal" : "") + (f.kind === "wood" ? " ck-chip--wood" : "") + "" + '" style="--chip:' + f.hex + (chip ? ";background-image:url(" + chip + ")" : "") + '"></span>' +
          '<span class="ck-sw-name">' + esc(f.baseName || f.name) + '</span>' + (f.price > 0 ? '<span class="ck-sw-price">+' + money(cfg, f.price) + "</span>" : ""),
          "ck-swatch");
      }).join("");
    }

    // One colour step: separate finish groups (Gloss, Matt, Wood veneer, Metal), then the swatches.
    // Solid colours appear under both Gloss and Matt; picking one sets its sheen.
    var GROUPS = [["gloss", "Gloss"], ["matt", "Matt"], ["wood", "Wood veneer"], ["metal", "Metal"]];
    function picker(zone, list, current) {
      var has = function (t) {
        return list.some(function (f) { return (t === "gloss" || t === "matt") ? f.kind === "colour" : f.kind === t; });
      };
      var groups = GROUPS.filter(function (g) { return has(g[0]); });
      var active = st[zone + "Tab"] || tabOf(current);
      if (!groups.some(function (g) { return g[0] === active; })) active = groups[0][0];
      var shown = (active === "gloss" || active === "matt")
        ? list.filter(function (f) { return f.kind === "colour"; }).map(function (f) { return withSheen(f, active); })
        : list.filter(function (f) { return f.kind === active; });
      var bar = '<div class="ck-picker-bar">';
      if (groups.length > 1) {
        bar += '<div class="ck-seg" role="group" aria-label="Finish">' + groups.map(function (g) {
          return '<button type="button" class="ck-seg-btn" data-zone="' + zone + '" data-tab="' + g[0] + '" aria-pressed="' + (g[0] === active) + '">' + g[1] + "</button>";
        }).join("") + "</div>";
      }
      bar += "</div>";
      return bar + '<div class="ck-swatches">' + swatches(zone, shown, pickValue(current)) + "</div>";
    }

    // Lists are built without a selection, so their HTML only changes when the choices themselves
    // change (new design, new finish group); picking an option just moves the tick.
    function setHTML(el, html, value) {
      if (el.__html !== html) { el.innerHTML = html; el.__html = html; }
      if (value === undefined) return;
      Array.prototype.forEach.call(el.querySelectorAll("input[type=radio]"), function (i) { i.checked = i.value === value; });
    }
    function renderOptions(r) {
      var dStep = $("designs").closest(".ck-step");
      dStep.hidden = cfg.designs.length < 2;
      var thumbs = $("designs").__html;
      setHTML($("designs"), thumbs !== undefined ? thumbs : cfg.designs.map(function (d) {
        var from = Math.min.apply(null, d.sizes.map(function (z) { return z.price; }));
        var mini = d.thumb ? '<img src="' + esc(d.thumb) + '" alt="" loading="lazy">'
          : viewsOf(d).length
          ? '<img src="' + esc(viewsOf(d)[0].photo) + '" alt="">'
          : drawTable(uid + "m" + d.id, resolve(cfg, { design: d.id, body: st.body, stand: st.stand, cloth: st.cloth }));
        return radio("design", d.id, false,
          '<span class="ck-thumb">' + mini + '</span><span class="ck-d-name">' + esc(d.name) + '</span><span class="ck-d-blurb">' + esc(d.blurb) + '</span><span class="ck-d-from">from ' + money(cfg, from) + "</span>",
          "ck-design");
      }).join(""), r.design.id);
      setHTML($("sizes"), r.design.sizes.map(function (z) {
        var m = /^(\d+\s*ft)\s*(.*)$/i.exec(z.label);
        var detail = [m ? m[2] : "", z.bed].filter(Boolean).join(" · ");
        return radio("size", z.id, false,
          '<span class="ck-size-label">' + esc(m ? m[1] : z.label) + '</span><span class="ck-size-bed">' + esc(detail) + '</span><span class="ck-size-price">' + (z.priceVaries ? "from " : "") + money(cfg, z.price) + "</span>",
          "ck-size");
      }).join(""), r.size.id);
      setHTML($("body"), picker("body", r.bodies, r.body), pickValue(r.body));
      setHTML($("stand"), picker("stand", r.stands, r.stand), pickValue(r.stand));
      var cl = $("cloth").__html;
      setHTML($("cloth"), cl !== undefined ? cl : swatches("cloth", cfg.cloths, r.cloth.id), r.cloth.id);
      $("topStep").hidden = !r.tops.length;
      $("delStep").hidden = !r.delivery;
      if (r.delivery) setHTML($("delivery"), r.deliveries.map(function (t) {
        return radio("delivery", t.id, false,
          '<span class="ck-top-name">' + esc(t.name) + '</span>' + (t.blurb ? '<span class="ck-top-blurb">' + esc(t.blurb) + '</span>' : '') +
          '<span class="ck-top-price">' + (t.enquiry ? "Enquiry only" : money(cfg, t.price)) + '</span>', "ck-top");
      }).join(""), r.delivery.id);
      $("stairsWrap").hidden = !r.stairCfg;
      if (r.stairCfg) {
        var opts = [];
        for (var f = 0; f <= r.stairCfg.max + 1; f++) opts.push(f);
        $("stairsNote").textContent = "· " + money(cfg, r.stairCfg.price) + " per flight";
        setHTML($("stairs"), opts.map(function (f) {
          var lab = f === 0 ? "None" : f > r.stairCfg.max ? (r.stairCfg.max + 1) + "+" : String(f);
          return radio("stairs", String(f), false, '<span>' + lab + '</span>', "ck-stair");
        }).join(""), String(r.flights));
      }
      if (r.tops.length) {
        setHTML($("top"), r.tops.map(function (t) {
          return checkbox("tops", t.id, false,
            '<span class="ck-top-name">' + esc(t.name) + '</span>' + (t.blurb ? '<span class="ck-top-blurb">' + esc(t.blurb) + '</span>' : '') +
            '<span class="ck-top-price">' + (t.price > 0 ? "+" + money(cfg, t.price) : "No extra cost") + '</span>', "ck-top ck-top--multi");
        }).join(""));
        Array.prototype.forEach.call($("top").querySelectorAll("input[type=checkbox]"), function (i) {
          i.checked = r.topsChosen.some(function (t) { return t.id === i.value; });
        });
      }
    }

    function renderView(r) {
      st.size = r.size.id; st.body = r.body.baseId || r.body.id; st.stand = r.stand.baseId || r.stand.id; st.cloth = r.cloth.id;
      if (r.body.sheen) st.bodySheen = r.body.sheen;
      if (r.stand.sheen) st.standSheen = r.stand.sheen;
      $("title").textContent = r.design.name;
      $("sub").textContent = [r.size.label, r.size.bed].filter(Boolean).join(" · ");
      var vs = viewsOf(r.design);
      if (st.view >= vs.length) st.view = 0;
      var token = ++paintToken, render = vs[st.view || 0];
      renderDots(vs.length);
      if (render) {
        var label = r.design.name + " pool table, " + r.bodyLabel.toLowerCase() + " " + r.body.name + ", " + r.standLabel.toLowerCase() + " " + r.stand.name + ", " + r.cloth.name + " cloth";
        var vkey = r.design.id + "/" + (st.view || 0);
        var existing = $("canvas").querySelector("canvas.ck-photo-canvas[data-view='" + vkey + "']");
        var fresh = !existing;
        if (fresh) {
          // a new design: paint it off screen and swap it in when ready, so the old picture stays until then
          var sz = render.size || [840, 280];
          existing = document.createElement("canvas");
          existing.className = "ck-photo-canvas ck-fade"; existing.setAttribute("data-design", r.design.id); existing.setAttribute("data-view", vkey); existing.setAttribute("role", "img");
          existing.width = sz[0] * SCALE; existing.height = sz[1] * SCALE;
          if (!$("canvas").firstChild) $("canvas").appendChild(existing);
        }
        existing.setAttribute("aria-label", label);
        var paintKey = [vkey, r.body.id, r.stand.id, r.cloth.id, r.topShown ? r.topShown.id : ""].join("|");
        if (existing.__paintKey !== paintKey) {                  // only the size or price changed: the picture stays
          var look = r.topShown ? (r.topShown.look || r.topShown.id) : "", cal = r.design.topcal;
          Promise.all([loadRender(render), loadWood(r.body.baseId ? null : r.body), loadWood(r.stand.baseId ? null : r.stand),
            look === "dining" && r.body.kind === "wood" && r.body.texture ? loadImage(r.body.texture).catch(function () { return null; }) : null]).then(function (all) {
            var data = all[0];
            if (token !== paintToken) return;
            if (existing.width !== data.w) { existing.width = data.w; existing.height = data.h; }
            paintPhoto(existing, data, render, r);
            if (look && cal && cal.m && cal.m[st.view || 0]) drawTop(existing, cal, st.view || 0, look, r.body, all[3]);
            existing.__paintKey = paintKey;
            if (existing.parentNode !== $("canvas")) $("canvas").replaceChildren(existing);
            if (!prefetched) { prefetched = true; prefetchDesigns(); }
          }).catch(function () {
            // image couldn't be read (e.g. blocked by the browser): fall back to the drawing
            if (token === paintToken) $("canvas").innerHTML = drawTable(uid, r);
          });
        }
      } else {
        $("canvas").innerHTML = drawTable(uid, r);
      }
      $("bodyLabel").textContent = r.bodyLabel;
      $("standLabel").textContent = r.standLabel;
      $("bodyName").textContent = "· " + r.body.name;
      $("standName").textContent = "· " + r.stand.name;
      $("clothName").textContent = "· " + r.cloth.name;
      $("designName").textContent = "· " + r.design.name.replace(/^Cue-King\s+|\s+Pool Table$/g, "");
      $("sizeName").textContent = "· " + r.size.label + " · " + money(cfg, r.variant.price);
      st.tops = r.topsChosen.map(function (t) { return t.id; }); st.topShown = r.topShown ? r.topShown.id : "";
      $("topName").textContent = "· " + (r.topsChosen.length ? r.topsChosen.map(function (t) { return t.name.replace(/\s*top$/i, ""); }).join(" + ") : "None");
      $("onTable").hidden = !r.topsChosen.length;
      setHTML($("onTableBtns"), r.topsChosen.map(function (t) {
        return '<button type="button" class="ck-ontable-btn" data-showtop="' + esc(t.id) + '">' + esc(t.name.replace(/\s*top$/i, "")) + '</button>';
      }).join("") + (r.topsChosen.length ? '<button type="button" class="ck-ontable-btn" data-showtop="">No top</button>' : ""));
      Array.prototype.forEach.call($("onTableBtns").querySelectorAll("[data-showtop]"), function (b) {
        b.setAttribute("aria-pressed", String(b.getAttribute("data-showtop") === st.topShown));
      });
      if (r.delivery) {
        st.delivery = r.delivery.id; st.stairs = String(r.flights);
        $("delName").textContent = "· " + r.delivery.name + (r.delEnquire ? " (enquiry)" : "") + (r.flights ? (r.stairsEnquire ? ", " + (r.stairCfg.max + 1) + "+ flights of stairs" : ", " + r.flights + (r.flights > 1 ? " flights" : " flight") + " of stairs") : "");
        $("enquire").hidden = !r.enquire;
        if (r.enquire) $("enquire").innerHTML = (r.delEnquire ? "Delivery and fitting outside UK mainland is quoted for each address. "
          : "For more than " + r.stairCfg.max + " flights of stairs we need to look at access first. ") +
          '<a href="' + esc(cfg.contactUrl || "/pages/contact") + '">Contact us for a quote</a>.';
        if ($("notes").value !== (st.notes || "")) $("notes").value = st.notes || "";
      }
      $("spec").innerHTML = [[r.bodyLabel, r.body], [r.standLabel, r.stand], ["Cloth", r.cloth]].map(function (p) {
        return '<div><dt>' + p[0] + '</dt><dd><span class="ck-dot" style="--chip:' + p[1].hex + '"></span>' + esc(p[1].name) + "</dd></div>";
      }).join("");
      var lines = priceLines(cfg, r), total = 0;
      $("lines").innerHTML = lines.map(function (l) {
        total += l.amount;
        return '<li><span>' + esc(l.label) + "</span><span>" + (l.base ? money(cfg, l.amount) : (l.amount ? "+" + money(cfg, l.amount) : "Included")) + "</span></li>";
      }).join("");
      $("total").textContent = money(cfg, total);
      $("payload").hidden = true;
      var miss = mode === "shopify" ? missingIds(r) : [];
      $("add").disabled = miss.length > 0 || r.enquire;
      $("status").textContent = r.delEnquire ? "Outside UK mainland: please contact us for a delivery quote."
        : r.enquire ? "More than " + r.stairCfg.max + " flights of stairs: please contact us for a quote."
        : miss.length ? "This build can't be ordered online yet. Please contact us to order it." : "";
      $("status").className = "ck-status" + (miss.length || r.enquire ? " ck-status--warn" : "");
    }

    // ---------- photo angles: arrows, dots, and swiping on the picture ----------
    function renderDots(n) {
      $("photos").classList.toggle("ck-photos--multi", n > 1);
      var html = "";
      for (var k = 0; k < n; k++) html += '<button type="button" class="ck-dot-btn" data-goto="' + k + '" aria-label="Angle ' + (k + 1) + ' of ' + n + '" aria-pressed="' + (k === (st.view || 0)) + '"></button>';
      if ($("dots").__html !== html) { $("dots").innerHTML = html; $("dots").__html = html; }
    }
    function goView(k) {
      var n = viewsOf(resolve(cfg, st).design).length;
      if (n < 2) return;
      st.view = ((k % n) + n) % n;
      update(false);
    }
    $("photos").addEventListener("click", function (e) {
      var s = e.target.closest("[data-step]"), g = e.target.closest("[data-goto]");
      if (s) goView((st.view || 0) + +s.getAttribute("data-step"));
      else if (g) goView(+g.getAttribute("data-goto"));
    });
    (function swipe(el) {
      var x0 = null, y0 = 0, t0 = 0;
      el.addEventListener("pointerdown", function (e) { if (e.target.closest("button")) return; x0 = e.clientX; y0 = e.clientY; t0 = Date.now(); });
      el.addEventListener("pointerup", function (e) {
        if (x0 === null) return;
        var dx = e.clientX - x0, dy = e.clientY - y0; x0 = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.3 && Date.now() - t0 < 900) goView((st.view || 0) + (dx < 0 ? 1 : -1));
      });
      el.addEventListener("pointercancel", function () { x0 = null; });
      el.addEventListener("keydown", function (e) {
        if (e.key === "ArrowRight") goView((st.view || 0) + 1);
        if (e.key === "ArrowLeft") goView((st.view || 0) - 1);
      });
    })($("photos"));

    // once the first picture is up, quietly fetch the other designs' pictures so switching is quick
    var prefetched = false;
    function prefetchDesigns() {
      var list = [];
      cfg.designs.forEach(function (d) { viewsOf(d).forEach(function (rd) { if (!/^data:/.test(rd.photo)) list.push(rd.photo, rd.mask, rd.shade, rd.detail); }); });
      var idle = window.requestIdleCallback || function (f) { return setTimeout(f, 300); };
      (function next() { var u = list.shift(); if (!u) return; idle(function () { var im = new Image(); im.onload = im.onerror = next; im.src = u; }); })();
    }

    function update(full) {
      var r = resolve(cfg, st);
      if (full) renderOptions(r);
      renderView(r);
      lastR = r;
      if (spinOn) showSpin(r);
    }

    // ---------- 360° view: the same table as a 3D model you can drag round ----------
    var spinOn = false, spinReady = null, lastR = null;
    function spinState(r) {
      return { design: r.design.id, body: r.body.baseId || r.body.id, bodySheen: r.body.sheen || "gloss",
        stand: r.stand.baseId || r.stand.id, standSheen: r.stand.sheen || "gloss", cloth: r.cloth.id,
        top: r.topShown ? (r.topShown.look || r.topShown.id) : "" };
    }
    function loadSpin() {
      if (spinReady) return spinReady;
      spinReady = new Promise(function (ok, fail) {
        if (window.CK360) return ok();
        if (!cfg.spinSrc) return fail(new Error("no 360 script"));
        var s = document.createElement("script");
        s.src = cfg.spinSrc; s.async = true;
        s.onload = function () { window.CK360 ? ok() : fail(new Error("360 script did not load")); };
        s.onerror = function () { fail(new Error("360 script did not load")); };
        document.head.appendChild(s);
      }).then(function () {
        if (window.CK_SPIN) return;
        try { var c = document.createElement("canvas"); if (!(c.getContext("webgl2") || c.getContext("webgl"))) throw 0; }
        catch (e) { throw new Error("no webgl"); }
        window.CK_SPIN_STAGE = $("spin");
        window.CK_SPIN_CONFIG = {
          designs: cfg.designs.map(function (d) { return { id: d.id, name: d.name, zones: d.zones || {}, defaults: d.defaults || {} }; }),
          finishes: cfg.bodyFinishes, cloths: cfg.cloths
        };
        window.CK_SPIN_INITIAL = lastR ? spinState(lastR) : null;
        window.CK360.start();
      });
      return spinReady;
    }
    // the 360° view opens on the same angle, and at the same size, as the photo being looked at
    // The 360 opens at the photo's angle with the table the same size as in the photo. Zoom per design
    // for [side, angle 2, angle 3], measured against the photos on a phone-shaped box (1.75:1); on a
    // wide box (2.2:1) the two angled views need 1.26 times more.
    var VIEW_ANGLES = [[0, 10], [36, 15], [-62, 10]], spinShown = null;
    var VIEW_ZOOM = { apex: [1, 0.93, 1], zigzag: [0.98, 1.05, 1.22], cross: [0.98, 1.08, 1.14], square: [0.98, 0.82, 0.85],
      xleg: [0.97, 0.86, 0.91], trapezoid: [0.95, 1.01, 1.12], uframe: [0.99, 0.89, 0.94], xbeam: [0.98, 0.83, 0.89],
      ultra: [0.99, 1.01, 1.06], roma: [0.99, 1.03, 1.04] };
    function viewZoom(id, v) {
      var el = root.querySelector(".ck-spin"), r = el && el.getBoundingClientRect();
      var k = r && r.height ? r.width / r.height : (matchMedia("(max-width: 600px)").matches ? 1.75 : 2.2);
      var t = Math.max(0, Math.min(1, (k - 1.75) / 0.45)), z = (VIEW_ZOOM[id] || [1, 1, 1])[v] || 1;
      return v ? z * (1 + 0.26 * t) : z;
    }
    function showSpin(r) {
      var K = window.CK_SPIN;
      if (!K) return;
      if (!K.show(r.design.id, spinState(r))) { setView("photo", true); return; }
      var key = r.design.id + "/" + (st.view || 0);
      if (spinShown !== key) {
        spinShown = key;
        var a = VIEW_ANGLES[st.view || 0] || VIEW_ANGLES[0];
        var v = st.view || 0, put = function () { K.place(a[0], a[1], K.fitDistance(a[0], a[1]) / viewZoom(r.design.id, v)); };
        K.setSpin(false); put();
        // once more after the 360 box has its final size, so the table matches the photo exactly
        requestAnimationFrame(function () { requestAnimationFrame(function () { if (spinShown === key) put(); }); });
      }
    }
    function setView(v, unavailable) {
      spinOn = v === "spin";
      root.querySelectorAll("[data-view3d]").forEach(function (b) { b.setAttribute("aria-pressed", String(b.getAttribute("data-view3d") === v)); });
      $("photos").hidden = spinOn; $("spin").hidden = !spinOn;
      $("viewHint").textContent = unavailable ? "360° view isn't available on this device"
        : spinOn ? (matchMedia("(pointer: coarse)").matches ? "Swipe sideways, then any way to turn · pinch to zoom" : "Drag any way to turn the table, over the top and underneath") : (viewsOf(resolve(cfg, st).design).length > 1 ? "Swipe for more angles" : "Tap 360° to turn it round");
      if (!spinOn) { spinShown = null; return; }
      loadSpin().then(function () {
        var m = $("spinMsg"); if (m) m.remove();
        if (lastR) showSpin(lastR);
      }).catch(function () { spinReady = null; setView("photo", true); });
    }
    // ---------- steps open one at a time, each showing its current choice when closed ----------
    // each step opens and closes on its own: any number can be open, and a step stays open until its
    // heading is clicked again
    function openStep(key) {
      root.querySelectorAll(".ck-step").forEach(function (fs) {
        var h = fs.querySelector("[data-toggle]");
        if (!h || h.getAttribute("data-toggle") !== key) return;
        var on = !fs.classList.contains("ck-open");
        fs.classList.toggle("ck-open", on);
        h.setAttribute("aria-expanded", String(on));
      });
    }
    root.addEventListener("click", function (e) {
      var t = e.target.closest("[data-toggle]");
      if (t) {
        var fs = t.closest(".ck-step"), wasOpen = fs.classList.contains("ck-open");
        openStep(t.getAttribute("data-toggle"));
        // keep the opened step in view on phones
        if (!wasOpen && t.getBoundingClientRect().top < 0) t.scrollIntoView({ block: "start", behavior: "smooth" });
        return;
      }
      var stb = e.target.closest("[data-showtop]");
      if (stb) { st.topShown = stb.getAttribute("data-showtop"); update(true); return; }
      var b = e.target.closest("[data-view3d]");
      if (b) setView(b.getAttribute("data-view3d"));
      var z = e.target.closest("[data-zoom]");
      if (z && window.CK_SPIN) window.CK_SPIN.zoom(+z.getAttribute("data-zoom"));
      var tl = e.target.closest("[data-tilt]");
      if (tl && window.CK_SPIN && window.CK_SPIN.tilt) window.CK_SPIN.tilt(+tl.getAttribute("data-tilt"));
    });

    form.addEventListener("input", function (e) {
      if (e.target.getAttribute && e.target.getAttribute("data-ck") === "notes") st.notes = e.target.value;   // kept as typed, sent with the order
    });

    form.addEventListener("change", function (e) {
      var t = e.target;
      if (!t.name) return;
      var key = t.name.slice(uid.length + 1);
      if (key === "tops") {
        st.tops = Array.prototype.map.call(root.querySelectorAll('input[name="' + t.name + '"]:checked'), function (i) { return i.value; });
        if (t.checked) st.topShown = t.value;                      // a newly added top goes straight on the table
        else if (st.topShown === t.value) st.topShown = st.tops.length ? st.tops[st.tops.length - 1] : "";
        update(true);
        var same = root.querySelector('input[name="' + t.name + '"][value="' + t.value + '"]');
        if (same) same.focus({ preventScroll: true });
        return;
      }
      st[key] = t.value;
      if (key === "design") { st.bodyTab = st.standTab = null; st.bodySheen = st.standSheen = null; st.view = 0; }
      if ((key === "body" || key === "stand") && t.value.indexOf("|") > 0) {
        var parts = t.value.split("|");
        st[key] = parts[0]; st[key + "Sheen"] = parts[1];
      }
      if (key === "body" || key === "stand") st[key + "Tab"] = null;
      // swatches in the design cards follow the chosen colours, so redraw options on colour changes too
      update(true);
      var again = root.querySelector('input[name="' + t.name + '"]:checked');
      if (again) again.focus({ preventScroll: true });
    });

    form.addEventListener("click", function (e) {
      var b = e.target.closest("[data-tab]");
      if (!b) return;
      var zone = b.getAttribute("data-zone");
      st[zone + "Tab"] = b.getAttribute("data-tab");
      update(true);
      var again = root.querySelector('[data-zone="' + zone + '"][data-tab="' + b.getAttribute("data-tab") + '"]');
      if (again) again.focus({ preventScroll: true });
    });

    form.addEventListener("submit", function (e) {
      e.preventDefault();
      var r = resolve(cfg, st);
      var ref = "CK-" + Math.random().toString(36).slice(2, 8).toUpperCase();
      var items = cartItems(cfg, r, ref);
      var status = $("status"), btn = $("add");
      if (mode !== "shopify") {
        status.className = "ck-status";
        status.textContent = "Preview only. On your store this button adds the following to the basket and opens it:";
        $("payload").textContent = JSON.stringify({ items: items }, null, 2);
        $("payload").hidden = false;
        return;
      }
      btn.disabled = true; btn.textContent = "Adding…";
      fetch(routeRoot + "cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ items: items })
      }).then(function (res) {
        return res.json().then(function (data) { return { ok: res.ok, data: data }; });
      }).then(function (out) {
        if (!out.ok) throw new Error(out.data.description || out.data.message || "The store refused the item");
        btn.textContent = "Added";
        window.location.href = cfg.cartUrl || routeRoot + "cart";
      }).catch(function (err) {
        btn.disabled = false; btn.textContent = "Add to basket";
        status.className = "ck-status ck-status--warn";
        status.textContent = "Couldn't add to basket: " + err.message + ". Please try again, or contact us to order this build.";
      });
    });

    update(true);
    }
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll("[data-ck-builder]"), function (el) {
      if (!el.__ck) { el.__ck = true; mount(el); }
    });
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot); else boot();
  document.addEventListener("shopify:section:load", boot);
  if (typeof window !== "undefined") window.CK_TOP_TEXTURE = topTexture;     // the 360 view draws its tops with the same pictures
  window.CueKingBuilder = { boot: boot, _test: { finishColour: finishColour, toSrgb: toSrgb, chipImage: chipImage, lightFinish: lightFinish, withSheen: withSheen, cartItems: cartItems, resolve: resolve, priceLines: priceLines, missingIds: missingIds, money: money, sizesFromProduct: sizesFromProduct, chooseVariant: chooseVariant, woodTone: woodTone, loadWood: loadWood, sampleWood: sampleWood } };
})();
